import {
	IsaAnalysisContent,
	IsaAnalysisMetrics,
	IsaAnalysisRequest,
	IsaAnalysisResponse,
	IsaTeamAnalysisRequest,
	PerformanceProcess,
	PerformanceProcessResponse,
	ProcessSummary,
} from "../proceso-desempeno.model";
import {
	Goal,
	TeamReviewee,
} from "../models/objetivos.model";
import {
	CompetencyEvaluation,
	CompetencyEvaluationTeam,
	CompetencyLevel,
} from "../evaluacion-competencias.model";
import {
	ApiFeedbackRecording,
	FeedbackBlock,
	FeedbackMeetingRequest,
	FeedbackPointsProgress,
	FeedbackStatus,
	FeedbackTeamRow,
} from "../feedback.model";
import {
	LatestProcessesResponse,
	PerformanceAnnouncementsResponse,
	PerformanceHomeResponse,
	PerformanceProfileResponse,
} from "../performance-home.model";
import { Checkin, CheckinStoreRequest, CheckinUpdateRequest } from "../checkin.model";
import { PerformanceSurvey, SurveySubmission } from "../survey.model";
import { ApiAttachment } from "../performance-attachments";
import {
	TeamTrackingIsaContent,
	TeamTrackingIsaSummaryResponse,
	TeamTrackingObjective,
	TeamTrackingRevieweeResponse,
	TeamTrackingRiskResponse,
	TeamTrackingSummary,
	TeamTrackingTeamMember,
	TeamTrackingTeamSummaryResponse,
} from "../team-tracking.model";

/**
 * Catálogo de estados de la sesión de feedback. El backend real lo administra
 * desde el gestor; aquí se replican los tres `code` que documenta el filtro del
 * listado (`pending`, `finished`, `cancelled`) para ver los colores en la fila.
 *
 * TODO (backend): `cancelled` existe en el catálogo pero **ninguna API de la app
 * puede producirlo**: falta el endpoint para cancelar una sesión.
 */
const FEEDBACK_STATUS_PENDING: FeedbackStatus = { id: 3, name: "Pendiente", code: "pending", color: "#FFA500" };
const FEEDBACK_STATUS_SCHEDULED: FeedbackStatus = { id: 2, name: "Pendiente", code: "pending", color: "#5358E8" };
const FEEDBACK_STATUS_STARTED: FeedbackStatus = { id: 5, name: "En curso", code: "started", color: "#5358E8" };
const FEEDBACK_STATUS_DONE: FeedbackStatus = { id: 1, name: "Realizada", code: "finished", color: "#00A86B" };
const FEEDBACK_STATUS_CANCELLED: FeedbackStatus = { id: 4, name: "Cancelada", code: "cancelled", color: "#9CA3AF" };

/**
 * Estado de un objetivo dentro del resultado del detalle-guion. Ojo: es la
 * aprobación del objetivo **en la etapa de creación**, no que se haya cumplido
 * (ver {@link FeedbackResultObjective}). Su `color` llega como nombre de la
 * paleta del gestor, no en hex.
 */
const FEEDBACK_OBJECTIVE_STATUS_APPROVED: FeedbackStatus = { id: 90, name: "Aprobado", code: "approved", color: "indigo-8" };

/**
 * Store en memoria del simulador de API del módulo de desempeño.
 *
 * Sembrado con la data que antes estaba inline en los servicios del módulo.
 * Mantiene estado mutable durante la sesión: los POST/PUT/DELETE que llegan por
 * el interceptor leen y mutan estos arrays, así que los cambios persisten hasta
 * recargar la página (reemplaza a los BehaviorSubject mutables de los servicios).
 *
 * El interceptor crea una única instancia (singleton de módulo) y se la pasa a
 * los handlers de rutas.
 */
export class PerformanceMockDb {
	/** Contador para asignar ids a los objetivos creados. */
	private nextGoalId = 100;

	/** Contador para asignar ids a los seguimientos (tracking) registrados. */
	private nextTrackingId = 3;

	/**
	 * Historial de seguimientos (tracking) registrados por objetivo. Forma cruda de
	 * cada item del listado `.../objectives/:objectiveId/tracking/list`. Sembrado con
	 * un registro para el objetivo aprobado (id 35) para que el modal de seguimiento
	 * muestre la evidencia previa al abrirse.
	 */
	private objectiveTrackings: any[] = [
		{
			id: 2,
			process_id: 1,
			reviewee_id: 11,
			objective_id: 35,
			self_review_progress_percentage: 90,
			self_review_progress_value: "18.00",
			self_review_progress_comment: "Avance al cierre de marzo",
			attachments: [
				{
					id: 901,
					name: "cierre-marzo.pdf",
					url: "https://cursalab-cdn.sfo3.digitaloceanspaces.com/mock/cierre-marzo.pdf",
					mime: "application/pdf",
					size: 128,
					size_label: "128 KB",
					uploaded_at: "2026-06-24T23:18:27.000000Z",
				},
			],
			created_at: "2026-06-24T23:18:27.000000Z",
			updated_at: "2026-06-24T23:22:52.000000Z",
			deleted_at: null,
		},
	];

	/** Contador para asignar ids a las evidencias de seguimiento subidas. */
	private nextTrackingAttachmentId = 902;

	// ---- Procesos -----------------------------------------------------------

	private processSummaries: ProcessSummary[] = [
		{ id: 1,
      name: "Proceso Tiendas Lima mock",
      status: {
        "id": 3,
        "name": "En curso",
        "code": "running"
      },
      schedule_finishes_at: "2024-03-31",
    },
		{ id: 2,
      name: "Proceso Tiendas Arequipa mock",
      status: {
        "id": 3,
        "name": "En curso",
        "code": "running"
      },
      schedule_finishes_at: "2024-03-31",
    },
	];

	/** Proceso completo con sus etapas (el mock devuelve siempre este), forma cruda de la API real. */
	private process: PerformanceProcess = {
    allow_attachments: true,
    max_multimedia: 5,
		id: 1,
		name: "Name mock",
		description: "Proceso de evaluación de desempeño anual",
		schedule_starts_at: "2026-01-01",
		schedule_finishes_at: "2026-12-31",
    reviewees_count: 45,
		// Roles del usuario de la sesión en este proceso. El backend los informa por
		// proceso y definen qué actividades se muestran en cada etapa (ver
		// ProcesoDesempenoService.resolveProcessRoles / normaliceStagesData):
		// - is_reviewee: es evaluado    → actividades individuales.
		// - is_reviewer: evalúa a otros → actividades de su equipo.
		// No son excluyentes. Para previsualizar cada flujo:
		// - solo colaborador → is_reviewee: true,  is_reviewer: false
		// - solo evaluador   → is_reviewee: false, is_reviewer: true
		// - líder evaluado   → ambas en true (valor por defecto del mock)
		is_reviewee: true,
		is_reviewer: true,
		roles: ["reviewee", "reviewer"],
		progress_stage: { id: 1, name: "Fijación de objetivos" },
		progress_stage_status: { id: 8, name: "En curso" },
		stages: [
			{
				id: 1,
				active: true,
				schedule_starts_at: "",
				schedule_finishes_at: "",
				started_at: "",
				finished_at: "",
				stage: { id: 1, code: "objectives", name: "Fijación de objetivos", position: 2 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "progreso",
            "position": 3
        }
			},
			// {
			// 	id: 1,
			// 	active: true,
			// 	schedule_starts_at: "",
			// 	schedule_finishes_at: "",
			// 	started_at: "",
			// 	finished_at: "",
			// 	stage: { id: 1, code: "objectives", name: "Fijación de objetivos", position: 1 },
      //   status : {
      //       "id": 1,
      //       "code": "finished",
      //       "name": "Finalizado",
      //       "position": 3
      //   }
			// },
			{
				id: 2,
				active: true,
				schedule_starts_at: "",
				schedule_finishes_at: "2026-06-20",
				// schedule_starts_at: "2026-06-21",
				started_at: "",
				finished_at: "",
				stage: { id: 2, code: "tracking", name: "Seguimiento", position: 3 },
        // Para probar el seguimiento en SOLO LECTURA, cambiar "code" a "finished":
        // entonces canTrackObjectives() devuelve false y el modal se abre sin
        // permitir adjuntar/enviar evidencia.
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "progresp",
            "position": 3
        }
			},
			{
				id: 3,
				active: true,
				schedule_starts_at: "2026-06-26",
				schedule_finishes_at: "2026-06-28",
				started_at: "2026-04-01",
				finished_at: null,
				stage: { id: 3, code: "evaluation", name: "Evaluación", position: 4 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "progresp",
            "position": 3
        }
			},
			{
				// Etapa "Competencias": solo consulta (el usuario ve las competencias
				// que le corresponden). Llega en el mismo array de etapas.
				id: 79,
				active: true,
				schedule_starts_at: null,
				schedule_finishes_at: null,
				started_at: null,
				finished_at: null,
				stage: { id: 113, code: "competencies", name: "Competencias", position: 1 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "En progreso",
            "position": 1
        }
			},
			{
				id: 5,
				active: true,
				schedule_starts_at: "2026-07-01",
				schedule_finishes_at: "2026-10-31",
				started_at: null,
				finished_at: null,
				stage: { id: 5, code: "feedback", name: "Feedback", position: 6 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "progresp",
            "position": 3
        }
			},
			{
				id: 6,
				active: true,
				schedule_starts_at: "2026-06-01",
				schedule_finishes_at: "2026-12-31",
				started_at: null,
				finished_at: null,
				stage: { id: 6, code: "pdi", name: "PDI", position: 7 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "progresp",
            "position": 3
        }
			},
			{
				// Etapa "Calibración": actividad única, solo para el líder. Va después de
				// la evaluación (calibra su resultado) y antes del feedback.
				// TODO (backend): `code` confirmado, el resto del bloque es provisional.
				id: 8,
				active: true,
				schedule_starts_at: "2026-06-29",
				schedule_finishes_at: "2026-10-31",
				started_at: null,
				finished_at: null,
				stage: { id: 8, code: "calibration", name: "Calibración", position: 5 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "En progreso",
            "position": 3
        }
			},
			{
				// Etapa "Encuesta": actividad única (responder la encuesta del proceso).
				// Suele ser la última del proceso.
				// TODO (backend): `code` provisional, a confirmar cuando publiquen la etapa.
				id: 7,
				active: true,
				schedule_starts_at: "2026-07-01",
				schedule_finishes_at: "2026-12-31",
				started_at: null,
				finished_at: null,
				stage: { id: 7, code: "survey", name: "Encuesta", position: 8 },
        status : {
            "id": 1,
            "code": "in-progress",
            "name": "En progreso",
            "position": 3
        }
			},
		],
		reviewee: {
			id: 1,
			process_id: 1,
			reviewee_id: 1,
			score: null,
			objectives_score: null,
			competencies_score: null,
		},
	};

	/**
	 * Análisis de avance (ISA) por etapa. El backend genera el texto con IA; el mock
	 * devuelve un análisis distinto según el `stage_id` que pida la vista, para poder
	 * previsualizar que cada pantalla muestra su propio resumen. La llave es el
	 * `stage_id` (taxonomía) y `general` el análisis sin etapa.
	 */
	private isaAnalysisByStage: Record<string, { stage_code: string | null; stage_name: string | null; metrics: IsaAnalysisMetrics; analysis: IsaAnalysisContent }> = {
		general: {
			stage_code: null,
			stage_name: null,
			metrics: {
				status: "En curso",
				days_remaining: 12,
				closes_at: "2026-08-15",
				stages_total: 5,
				stages_completed: 3,
				general_score: 4.2,
				max_score: 5,
			},
			analysis: {
				status_label: "Estado: En curso",
				risk_level: "low",
				headline: "Vas al día con tu proceso",
				summary:
					"<p>Has completado <strong>3 de 5 etapas</strong> del proceso, con una nota general de <strong>4.2 de 5</strong>. Mantén el ritmo para cerrar a tiempo.</p>",
				highlights: [
					{ label: "Tiempo restante", value: "12 días", tone: "warning", hint: "El proceso cierra el 15/08/2026." },
					{ label: "Nota general", value: "4.2 de 5", tone: "positive", hint: "Por encima del promedio esperado." },
					{ label: "Etapas completadas", value: "3 de 5", tone: "neutral" },
				],
				recommendations: ["Revisa las etapas pendientes antes del cierre del proceso."],
			},
		},
		objectives: {
			stage_code: "objectives",
			stage_name: "Definición de objetivos",
			metrics: { status: "En curso", days_remaining: 6, closes_at: "2026-08-02" },
			analysis: {
				status_label: "Estado: En curso",
				risk_level: "medium",
				headline: "Te faltan objetivos por enviar",
				summary:
					"<p>Registraste <strong>3 objetivos</strong>, pero tu plan aún <strong>no suma el 100%</strong> del peso requerido. Complétalo antes del cierre de la etapa.</p>",
				highlights: [
					{ label: "Tiempo restante", value: "6 días", tone: "warning", hint: "La etapa cierra el 02/08/2026." },
					{ label: "Peso asignado", value: "70%", tone: "warning", hint: "Falta distribuir 30% entre tus objetivos." },
					{ label: "Objetivos aprobados", value: "1 de 3", tone: "neutral" },
				],
				recommendations: [
					"Distribuye el 30% de peso restante entre tus objetivos.",
					"Envía tu plan a tu líder para que lo valide.",
				],
			},
		},
		evaluation: {
			stage_code: "evaluation",
			stage_name: "Evaluación",
			metrics: {
				status: "En curso",
				days_remaining: 11,
				closes_at: "2026-08-07",
				team_total_reviews: 8,
				team_completed_reviews: 5,
				team_review_percentage: 63,
				pending_users: 2,
			},
			analysis: {
				status_label: "Estado: En curso",
				risk_level: "medium",
				headline: "Buen avance, revisiones de equipo pendientes",
				summary:
					"<p>Vas <strong>al día</strong> con tu autoevaluación. Aún tienes <strong>2 evaluados</strong> por revisar antes del cierre.</p>",
				highlights: [
					{ label: "Tiempo restante", value: "11 días", tone: "warning", hint: "La etapa cierra el 07/08/2026." },
					{ label: "Revisiones de equipo", value: "63%", tone: "neutral", hint: "5 de 8 evaluaciones completadas." },
					{ label: "Evaluados pendientes", value: "2", tone: "warning" },
				],
				recommendations: ["Completa las 2 evaluaciones de equipo pendientes antes del cierre."],
			},
		},
		feedback: {
			stage_code: "feedback",
			stage_name: "Feedback",
			metrics: { status: "En curso", days_remaining: 9, closes_at: "2026-08-05", team_total_reviews: 8, team_completed_reviews: 3 },
			analysis: {
				status_label: "Estado: En curso",
				risk_level: "high",
				headline: "Faltan reuniones de feedback por agendar",
				summary:
					"<p>Agendaste <strong>3 de 8</strong> reuniones de feedback. Coordina las restantes para poder cerrar la etapa a tiempo.</p>",
				highlights: [
					{ label: "Tiempo restante", value: "9 días", tone: "warning", hint: "La etapa cierra el 05/08/2026." },
					{ label: "Reuniones agendadas", value: "3 de 8", tone: "warning", hint: "5 integrantes sin horario definido." },
				],
				recommendations: [
					"Agenda las 5 reuniones de feedback pendientes.",
					"Revisa los resultados de cada integrante antes de la reunión.",
				],
			},
		},
	};

	/**
	 * Data del Home del módulo de desempeño (actividad en curso destacada +
	 * actividades por proceso). El backend definirá la forma final; por ahora el
	 * mock devuelve siempre la misma data envuelta en `{ data }`. La sección de
	 * "Novedades" no vive aquí: reutiliza el endpoint de anuncios.
	 */
	private performanceHome: PerformanceHomeResponse = {
		data: {
			// Cada proceso trae SU propia data (métricas, avance, actividades...). La
			// vista muestra solo un proceso a la vez; el selector "Estás en: [proceso]"
			// aparece cuando hay más de uno. Con un único proceso el selector se oculta.
			processes: [
				{
					id: 1,
					name: "Proceso 01",
					nextActivity: {
						statusLabel: "EN CURSO",
						title: "Evaluando a mi jefe",
						remainingLabel: "5 días restantes",
						dueDateLabel: "Vence el 30 de Junio",
						progressPercent: 60,
						actionLabel: "Continuar ahora",
						route: "proceso/1",
					},
					// Métricas del header (solo desktop). Mismos indicadores que
					// ProcesoEtapasPage.headerMetrics hasta que el backend confirme el endpoint.
					headerMetrics: [
						{
							icon: "mdi mdi-clock-outline",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "Tiempo restante",
							value: "12 días",
						},
						{
							icon: "mdi mdi-check-circle-outline",
							iconClass: "bg-green-200 text-green-600",
							label: "Revisión de tu equipo",
							value: "85%",
						},
						{
							icon: "mdi mdi-comment-text-outline",
							iconClass: "bg-orange-200 text-orange-500",
							label: "Cambios solicitados",
							value: "14 items",
						},
					],
					// Tarjeta "Organización" (solo desktop): líder + equipo del usuario.
					organization: {
						leaderLabel: "Líder",
						leader: { name: "Carlos Ramos" },
						teamLabel: "Mi equipo",
						teamMembers: [
							{ name: "Ana Torres" },
							{ name: "Luis Vega" },
							{ name: "María Díaz" },
							{ name: "Jorge Salas" },
						],
						activeCount: 10,
						activeLabel: "Participantes activos",
					},
					// Avance del proceso (solo desktop): stepper de etapas de este proceso.
					processProgress: {
						processId: 1,
						processName: "Proceso 01",
						route: "proceso/1",
						stages: [
							{ name: "Creación de objetivos", icon: "mdi mdi-target", state: "completed" },
							{ name: "Revisión de objetivos", icon: "mdi mdi-clipboard-check-outline", state: "active" },
							{ name: "Seguimiento de objetivos", icon: "mdi mdi-chart-line", state: "locked" },
							{ name: "Evaluación de competencias", icon: "mdi mdi-account-star-outline", state: "locked" },
							{ name: "PDI", icon: "mdi mdi-flag-outline", state: "locked" },
						],
					},
					activities: [
						{
							icon: "mdi mdi-target",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "OBJETIVOS",
							pendingCount: 2,
							totalCount: 36,
							route: "proceso/1",
						},
						{
							icon: "mdi mdi-chart-line",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "COMPETENCIAS",
							pendingCount: 3,
							totalCount: 20,
							route: "proceso/1",
						},
					],
				},
				{
					id: 2,
					name: "Proceso 02",
					nextActivity: {
						statusLabel: "EN CURSO",
						title: "Autoevaluación de competencias",
						remainingLabel: "9 días restantes",
						dueDateLabel: "Vence el 15 de Julio",
						progressPercent: 30,
						actionLabel: "Continuar ahora",
						route: "proceso/2",
					},
					headerMetrics: [
						{
							icon: "mdi mdi-clock-outline",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "Tiempo restante",
							value: "9 días",
						},
						{
							icon: "mdi mdi-check-circle-outline",
							iconClass: "bg-green-200 text-green-600",
							label: "Revisión de tu equipo",
							value: "40%",
						},
						{
							icon: "mdi mdi-comment-text-outline",
							iconClass: "bg-orange-200 text-orange-500",
							label: "Cambios solicitados",
							value: "3 items",
						},
					],
					organization: {
						leaderLabel: "Líder",
						leader: { name: "Carlos Ramos" },
						teamLabel: "Mi equipo",
						teamMembers: [{ name: "Ana Torres" }, { name: "Luis Vega" }],
						activeCount: 6,
						activeLabel: "Participantes activos",
					},
					processProgress: {
						processId: 2,
						processName: "Proceso 02",
						route: "proceso/2",
						stages: [
							{ name: "Creación de objetivos", icon: "mdi mdi-target", state: "completed" },
							{ name: "Revisión de objetivos", icon: "mdi mdi-clipboard-check-outline", state: "completed" },
							{ name: "Seguimiento de objetivos", icon: "mdi mdi-chart-line", state: "active" },
							{ name: "Evaluación de competencias", icon: "mdi mdi-account-star-outline", state: "locked" },
							{ name: "PDI", icon: "mdi mdi-flag-outline", state: "locked" },
						],
					},
					activities: [
						{
							icon: "mdi mdi-target",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "OBJETIVOS",
							pendingCount: 0,
							totalCount: 12,
							route: "proceso/2",
						},
						{
							icon: "mdi mdi-chart-line",
							iconClass: "bg-primary_ultra_light_variant text-primary",
							label: "COMPETENCIAS",
							pendingCount: 5,
							totalCount: 20,
							route: "proceso/2",
						},
					],
				},
			],
		},
	};

	// ---- Objetivos ----------------------------------------------------------

  //get my goals
	private myGoals: any = [
    {
      "id": 33,
      "name": "Incremento de ventas Q1",
      "description": "Lograr un incremento del 20% en ventas durante el primer trimestre.",
      "measurement_unit": {
        "id": 40,
        "code": null,
        "name": "Porcentaje"
      },
      "measurement_metric": {
        "id": 17,
        "code": "three-markers",
        "name": "Tres marcadores"
      },
      "evaluation_type": {
        "id": 19,
        "code": "direct",
        "name": "Directo"
      },
      "measurement_value": 20,
      // Un valor por cada marcador hijo de la métrica (tres-marcadores: 27/28/29),
      // real en la unidad de la meta, como la meta misma.
      "measurement_metric_values": [
        { "taxonomy_id": 27, "value": 5 },
        { "taxonomy_id": 28, "value": 12 },
        { "taxonomy_id": 29, "value": 20 }
      ],
      "objective_weight": 40,
      "status": {
        "id": 1,
        "code": "sent",
        "name": "Pendiente de revisión",
        "color": "#FF9800"
      } ,
      "approved": false,
      "starts_at": "2024-01-01",
      "finishes_at": "2024-03-31",
      "initiatives": [
        {
        "id": "isa-0",
        "active": "1",
        "description": "Realizar talleres mensuales para el equipo, mejorando habilidades y estrategias de ventas efectivas dentro del mes."
        },
        {
        "id": "isa-1",
        "active": "1",
        "description": "Rediseñar la disposición de productos en tienda para aumentar la atracción y compra, implementado en las primeras dos semanas."
        }
      ]
    },
    {
      "id": 34,
      "name": "Incremento de ventas Q2",
      "description": "Lograr un incremento del 20% en ventas durante el primer trimestre.",
      "measurement_unit": {
        "id": 40,
        "code": null,
        "name": "Porcentaje"
      },
      "measurement_metric": {
        "id": 17,
        "code": "three-markers",
        "name": "Tres marcadores"
      },
      "evaluation_type": {
        "id": 19,
        "code": "direct",
        "name": "Directo"
      },
      "measurement_value": 20,
      // Un valor por cada marcador hijo de la métrica (tres-marcadores: 27/28/29),
      // real en la unidad de la meta, como la meta misma.
      "measurement_metric_values": [
        { "taxonomy_id": 27, "value": 5 },
        { "taxonomy_id": 28, "value": 12 },
        { "taxonomy_id": 29, "value": 20 }
      ],
      "objective_weight": 40,
      "status": {
        "id": 2,
        "code": "rejected",
        "name": "rechazado",
        "color": "#FF9800"
      } ,
      "approved": false,
      "starts_at": "2024-01-01",
      "finishes_at": "2024-03-31",
      "initiatives": [
        {
        "id": "isa-0",
        "active": "1",
        "description": "Realizar talleres mensuales para el equipo, mejorando habilidades y estrategias de ventas efectivas dentro del mes."
        },
        {
        "id": "isa-1",
        "active": "1",
        "description": "Rediseñar la disposición de productos en tienda para aumentar la atracción y compra, implementado en las primeras dos semanas."
        }
      ]
    },
    {
      "id": 35,
      "name": "Incremento de ventas Q3",
      "description": "Lograr un incremento del 20% en ventas durante el primer trimestre.",
      "measurement_unit": {
        "id": 40,
        "code": null,
        "name": "Porcentaje"
      },
      "measurement_metric": {
        "id": 17,
        "code": "three-markers",
        "name": "Tres marcadores"
      },
      "evaluation_type": {
        "id": 19,
        "code": "direct",
        "name": "Directo"
      },
      "measurement_value": 20,
      // Un valor por cada marcador hijo de la métrica (tres-marcadores: 27/28/29),
      // real en la unidad de la meta, como la meta misma.
      "measurement_metric_values": [
        { "taxonomy_id": 27, "value": 5 },
        { "taxonomy_id": 28, "value": 12 },
        { "taxonomy_id": 29, "value": 20 }
      ],
      "objective_weight": 40,
      "status": {
        "id": 3,
        "code": "approved",
        "name": "Aprobado",
        "color": "#FF9800"
      } ,
      "approved": false,
      "starts_at": "2024-01-01",
      "finishes_at": "2024-03-31",
      "initiatives": [
        {
        "id": "isa-0",
        "active": "1",
        "description": "Realizar talleres mensuales para el equipo, mejorando habilidades y estrategias de ventas efectivas dentro del mes."
        },
        {
        "id": "isa-1",
        "active": "1",
        "description": "Rediseñar la disposición de productos en tienda para aumentar la atracción y compra, implementado en las primeras dos semanas."
        }
      ]
    },
    {
      "id": 335,
      "name": "Incremento de ventas Q4",
      "description": "Lograr un incremento de 15 unidades en ventas durante el segundo trimestre.",
      "measurement_unit": {
        "id": 42,
        "code": null,
        "name": "Unidad"
      },
      "measurement_metric": {
        "id": 17,
        "code": "three-markers",
        "name": "Tres marcadores"
      },
      "evaluation_type": {
        "id": 19,
        "code": "direct",
        "name": "Directo"
      },
      "measurement_value": 15,
      // Un valor por cada marcador hijo de la métrica (tres-marcadores: 27/28/29),
      // real en la unidad de la meta, como la meta misma.
      "measurement_metric_values": [
        { "taxonomy_id": 27, "value": 5 },
        { "taxonomy_id": 28, "value": 12 },
        { "taxonomy_id": 29, "value": 20 }
      ],
      "objective_weight": 40,
      "status": {
        "id": 3,
        "code": "approved",
        "name": "Aprobado",
        "color": "#FF9800"
      } ,
      "approved": false,
      "starts_at": "2024-01-01",
      "finishes_at": "2024-03-31",
      "initiatives": [
        {
        "id": "isa-0",
        "active": "1",
        "description": "Realizar talleres mensuales para el equipo, mejorando habilidades y estrategias de ventas efectivas dentro del mes."
        },
        {
        "id": "isa-1",
        "active": "1",
        "description": "Rediseñar la disposición de productos en tienda para aumentar la atracción y compra, implementado en las primeras dos semanas."
        }
      ]
    }
  ];


	/**
	 * Taxonomías para poblar el formulario de objetivo (unidades de medida, tipos
	 * de métrica con sus marcadores hijos y tipos de evaluación). Forma cruda del
	 * endpoint `.../me/processes/:id/objectives/get-form-data`.
	 */
	private objectiveFormData = {
		measurement_units: [
			{ color: null, icon: null, name: "Monetario", id: 39, code: null },
			{ color: null, icon: null, name: "Porcentaje", id: 40, code: null },
			{ color: null, icon: null, name: "Unidad", id: 41, code: null },
			{ color: null, icon: null, name: "Tiempo", id: 42, code: null },
		],
		measurement_metrics: [
			{
				color: null,
				icon: null,
				name: "Dos marcadores",
				id: 16,
				code: "two-markers",
				children: [],
			},
			{
				color: null,
				icon: null,
				name: "Tres marcadores",
				id: 17,
				code: "three-markers",
				children: [
					{ id: 27, parent_id: 17, group: "measurement", type: "metric-child", position: 1, code: null, name: "Mínimo inferior", value: null, icon: null, color: null, slug: "minimo-inferior", active: true, description: null, children: [] },
					{ id: 28, parent_id: 17, group: "measurement", type: "metric-child", position: 2, code: null, name: "Mínimo", value: null, icon: null, color: null, slug: "minimo", active: true, description: null, children: [] },
					{ id: 29, parent_id: 17, group: "measurement", type: "metric-child", position: 3, code: null, name: "Máximo", value: null, icon: null, color: null, slug: "maximo", active: true, description: null, children: [] },
				],
			},
      {
      color: null,
        icon: null,
        name: "Cuatro marcadores",
        id: 18,
        code: "four-markers",
        children: [
          { id: 30, parent_id: 18, group: "measurement", type: "metric-child", position: 1, code: null, name: "Mínimo inferior", value: null, icon: null, color: null, slug: "minimo-inferior", active: true, description: null, children: [] },
          { id: 31, parent_id: 18, group: "measurement", type: "metric-child", position: 2, code: null, name: "Mínimo", value: null, icon: null, color: null, slug: "minimo", active: true, description: null, children: [] },
          { id: 32, parent_id: 18, group: "measurement", type: "metric-child", position: 3, code: null, name: "Máximo", value: null, icon: null, color: null, slug: "maximo", active: true, description: null, children: [] },
          { id: 33, parent_id: 18, group: "measurement", type: "metric-child", position: 4, code: null, name: "Máximo superior", value: null, icon: null, color: null, slug: "maximo-superior", active: true, description: null, children: [] },
        ],
      }
		],
		evaluation_types: [
			{ color: null, icon: null, name: "Directo", id: 19, code: "direct" },
			{ color: null, icon: null, name: "Inverso", id: 20, code: "inverse" },
		],
	};

	private goals: Goal[] = [
		{
			id: 1,
			category: "Operaciones comerciales",
			description:
				"test obj 1 Lograr en el año 2026 en Gloria Foods del Uruguay USD 13.03 MM de ventas.",
			goalValue: 30,
			status: "pending",
			initiatives: [{ initiative: "inic 1" }],
			measurementScaleLowerMin: "11",
			measurementScaleMax: "44",
			measurementScaleMin: "22",
			measurementScaleObjective: "33",
			rangeDate: "08/04/2026 - 18/04/2026",
			unitOfMeasurement: "Porcentaje(%)",
			createdBy: "Carla Ramos Ramirez",
		},
		{
			id: 2,
			category: "Operaciones comerciales",
			description:
				"test obj 2 Lograr en las operaciones una Carga Fabril / venta no mayor a 14.32 % y Mermas + destrucciones / Venta",
			goalValue: 5,
			status: "pending",
			initiatives: [{ initiative: "inic 11" }],
			measurementScaleLowerMin: "11",
			measurementScaleMax: "44",
			measurementScaleMin: "22",
			measurementScaleObjective: "33",
			rangeDate: "08/04/2026 - 18/04/2026",
			unitOfMeasurement: "Porcentaje(%)",
			createdBy: "Carla Ramos Ramirez",
		},
		{
			id: 3,
			category: "Operaciones comerciales",
			description: "test obj 3 Operaciones Carga Fabril",
			goalValue: 5,
			status: "pending",
			initiatives: [{ initiative: "dfsdfs fsdfdsfs sdf" }, { initiative: "lorem ipsum" }],
			measurementScaleLowerMin: "11",
			measurementScaleMax: "44",
			measurementScaleMin: "22",
			measurementScaleObjective: "33",
			rangeDate: "08/04/2026 - 18/04/2026",
			unitOfMeasurement: "Porcentaje(%)",
			createdBy: "Kristel Ramos Ramirez",
		},
	];

	// ---- Equipo a cargo (Mi equipo) ----------------------------------------

	/**
	 * Evaluados a cargo del supervisor. Forma cruda del endpoint real
	 * `GET .../team/processes/:id/reviewees`: el estado es display-driven
	 * (`{ name, color }`, sin code). Sembrado con varios registros y estados
	 * variados para probar el chip, los filtros y el scroll infinito de la vista.
	 */
	private teamReviewees: TeamReviewee[] = PerformanceMockDb.buildTeamReviewees();

	/** Genera ~24 evaluados de mock con estados variados (para paginar y filtrar). */
	private static buildTeamReviewees(): TeamReviewee[] {
		const names = [
			"Aitor Casanova", "Joel Raya", "Jordi Cantú", "Lucas Tello", "Nicolás Gastélum",
			"Rosario Tovar", "Rocío Pantoja", "Juan José Rojo", "Rosa María Carrión", "Biel Escamilla",
			"Cristian Bermúdez", "Mario Deleón", "Daniel Reynoso", "Gael Benavides", "Celia Granados",
			"Francisca Calvo", "Joel Delatorre", "Mario Mercado", "Nora Paredes", "Francisco Gámez",
			"César Velasco", "Rubén Jimínez", "Iker Montenegro", "Leire Zelaya",
		];
		const statuses = [
			{ name: "—", color: "grey" },
			{ name: "Pendiente de revisión", color: "orange" },
			{ name: "Aprobado", color: "green" },
			{ name: "Observado", color: "red" },
		];
		return names.map((title, i) => ({
			id: i + 1,
			reviewee_id: 81 + i * 13,
			avatar_name: { image: `/avatars/user-${(i % 8) + 1}.png`, title },
			review_type_id: 15,
			review_type_name: "Evaluación del jefe",
			status: statuses[i % statuses.length],
		}));
	}

	/**
	 * Objetivos del plan de un integrante del equipo. Forma cruda del endpoint real
	 * `GET .../team/processes/:id/objectives`: el nombre y el tipo de evaluación
	 * vienen anidados en `objective`, el estado trae `code`, e `initiatives` es
	 * `[{ name, description }]`. La vista los normaliza en el servicio de dominio.
	 */
	private teamObjectives: any[] = PerformanceMockDb.buildTeamObjectives();

	private static buildTeamObjectives(): any[] {
		const threeMarkers = {
			id: 17, code: "three-markers", name: "Tres marcadores",
			children: [
				{ id: 27, parent_id: 17, group: "measurement", type: "metric-child", position: 1, code: null, name: "Mínimo inferior", value: null, icon: null, color: null, slug: "minimo-inferior", active: true, description: null, children: [] },
				{ id: 28, parent_id: 17, group: "measurement", type: "metric-child", position: 2, code: null, name: "Mínimo", value: null, icon: null, color: null, slug: "minimo", active: true, description: null, children: [] },
				{ id: 29, parent_id: 17, group: "measurement", type: "metric-child", position: 3, code: null, name: "Máximo", value: null, icon: null, color: null, slug: "maximo", active: true, description: null, children: [] },
			],
		};
		const directo = { id: 19, code: "direct", name: "Directo" };
		const inverso = { id: 20, code: "inverse", name: "Inverso" };
		const monetario = { id: 39, code: null, name: "Monetario" };
		const tiempo = { id: 42, code: null, name: "Tiempo" };
		const sent = { id: 89, name: "Pendiente de revisión", code: "sent", color: "amber-14" };
		const approved = { id: 90, name: "Aprobado", code: "approved", color: "green-14" };
		const reviewee = { id: 81, name: "Aitor", lastname: "Casanova", surname: "Casanova", fullname: "Aitor Casanova", document: null, email: null };

		const base = (id: number, over: any) => ({
			id, process_reviewee_id: null, process_id: 1, reviewee_id: 81, status_id: 89,
			objective_id: id, objective_weight: "40.00", measurement_unit_id: 39, measurement_value: "25.00",
			measurement_metric_id: 17,
			// Reales, en la unidad de la meta (25).
			measurement_metric_values: [ { value: 0, taxonomy_id: 27 }, { value: 12, taxonomy_id: 28 }, { value: 24, taxonomy_id: 29 } ],
			initiatives: [ { name: "Llamar a 10 clientes por semana", description: "Contacto directo con clientes potenciales" } ],
			initiatives_count: null,
			evidences: [
				{ id: 1, comment: "Se ha completado efectivamente el avance indicado en el objetivo según las fechas puestas.", file: null },
				{ id: 2, comment: "Adjunto el reporte del primer mes con los resultados obtenidos.", file: { name: "reporte-mensual.pdf", type: "application/pdf", dataUrl: null } },
			],
			description: "Lograr un 25% de crecimiento en ventas",
			starts_at: "2026-06-23T05:00:00.000000Z", finishes_at: "2026-06-30T05:00:00.000000Z",
			approved: 0, approved_at: null, created_at: "2026-06-23T04:26:11.000000Z", updated_at: "2026-06-23T04:26:11.000000Z", deleted_at: null,
			objective: { id, name: `Objetivo ${id}`, description: "Lograr un 25% de crecimiento en ventas", evaluation_type_id: 19, evaluation_type: directo },
			measurement_unit: monetario, measurement_metric: threeMarkers, reviewee, status: sent,
			...over,
		});

		return [
			base(10, { objective_weight: null, measurement_unit_id: 42, measurement_unit: tiempo, measurement_value: "15.00", description: "Reducir el tiempo promedio para cerrar una venta en la Zona Lourdes a un máximo de 15 días durante este periodo.", objective: { id: 10, name: "Reducir el tiempo promedio de cierre de ventas en Zona Lourdes", description: "Reducir el tiempo promedio de cierre.", evaluation_type_id: 20, evaluation_type: inverso } }),
			base(9, { measurement_unit_id: 42, measurement_unit: tiempo, measurement_value: "9.00", description: "descripción del objetivo lili", objective: { id: 9, name: "Objetivo prueba lili", description: "descripción del obj lili", evaluation_type_id: 20, evaluation_type: inverso } }),
			base(8, { objective_weight: "45.00", measurement_value: "30.00", description: "Lograr un 30% de crecimiento en ventas", objective: { id: 8, name: "Incrementar ventas Q2 (rev.)", description: "Lograr un 25% de crecimiento en ventas", evaluation_type_id: 19, evaluation_type: directo } }),
			base(6, { description: "Lograr un 25% de crecimiento en ventas", objective: { id: 6, name: "Incrementar ventas Q2", description: "Lograr un 25% de crecimiento en ventas", evaluation_type_id: 19, evaluation_type: directo } }),
			base(5, { status: approved, approved: 1, description: "Lograr un 25% de crecimiento en ventas", objective: { id: 5, name: "Incrementar ventas Q1", description: "Lograr un 25% de crecimiento en ventas", evaluation_type_id: 19, evaluation_type: directo } }),
			// Dos objetivos aprobados más: en la etapa de Seguimiento solo los
			// aprobados admiten registro de avance, y con uno solo no se podía
			// previsualizar la lista de la izquierda del detalle del líder.
			base(4, { status: approved, approved: 1, description: "Colocar la cartera asignada de la zona durante el periodo.", objective: { id: 4, name: "Colocación de cartera", description: "Colocar la cartera asignada de la zona.", evaluation_type_id: 19, evaluation_type: directo } }),
			base(7, { status: approved, approved: 1, measurement_unit_id: 42, measurement_unit: tiempo, measurement_value: "12.00", description: "Retener a los clientes clave de la zona durante el periodo.", objective: { id: 7, name: "Retención de clientes clave", description: "Retener a los clientes clave de la zona.", evaluation_type_id: 19, evaluation_type: directo } }),
		];
	}

	// ---- Evaluación de competencias ----------------------------------------

	private evaluationTeam: CompetencyEvaluationTeam = {
		leader: { id: 1, name: "Carla Ramos Ramos", role: "Técnico vendedor", status: "pending", avatarUrl: null },
		peers: [
			{ id: 2, name: "Carlos Ramos Palacios", role: "Técnico vendedor", status: "in_progress", avatarUrl: null },
			{ id: 3, name: "Deborah Robles", role: "Técnico vendedor", status: "pending", avatarUrl: null },
			{ id: 4, name: "María Sánchez", role: "Técnico vendedor", status: "completed", avatarUrl: null },
		],
	};

	private static readonly COMPETENCY_LEVELS: CompetencyLevel[] = [
		{ id: 1, level: 1, label: "Necesita mejora", percentage: 20 },
		{ id: 2, level: 2, label: "Parcialmente efectivo", percentage: 40 },
		{ id: 3, level: 3, label: "Efectivo", percentage: 80 },
		{ id: 4, level: 4, label: "Parcialmente exede", percentage: 90 },
		{ id: 5, level: 5, label: "Excede", percentage: 120 },
	];

	private competencyEvaluation: CompetencyEvaluation = {
		person: { id: 1, name: "Carla Ramos Ramos", role: "Técnico vendedor", status: "pending", avatarUrl: null },
		deadline: "2026-07-30",
		competencies: [
			"Análisis y solución de problemas",
			"Proactividad",
			"Orientación al cliente",
			"Orientación los resultados",
			"Liderazgo",
		].map((name, index) => ({
			id: index + 1,
			name,
			description:
				"Lograr en las operaciones una carga fabril/ venta no mayor a un 14.32% eb mermas + destrucciones / venta no mayor a 2.1% durante el trimestre.",
			levels: PerformanceMockDb.COMPETENCY_LEVELS,
			selectedLevelId: null,
			comment: null,
		})),
	};

	// ---- Feedback (vista del líder) ----------------------------------------
	// TODO (backend): data provisional. Reemplazar por la respuesta real cuando
	// el equipo de backend publique los endpoints de esta etapa.

	/**
	 * Equipo del líder en la etapa de feedback, **en la forma exacta que devuelve
	 * el endpoint real**: la sesión aplanada (`session_date`, `session_type`,
	 * `meeting_link` / `address`) y la fecha como `dd/MM/yyyy HH:mm`.
	 *
	 * Sin `session_date` = reunión sin configurar (el líder ve "Definir horario de
	 * reunión"); con fecha = reunión agendada (la fila muestra el resumen y navega
	 * al detalle).
	 *
	 * Las tres modalidades están representadas para poder probarlas:
	 * `"virtual"` (Zoom), `"presencial"` (con `address`) y `""` con `meeting_link`
	 * (enlace libre). Dos filas llegan con `feedback: null`, que es a quien hay que
	 * agendarle.
	 *
	 * ⚠️ **Se devuelven los huecos reales del backend**: ninguna sesión trae
	 * `address_reference`, `latitude`, `longitude` ni `allow_recording`, porque el
	 * `store` los acepta pero **ninguna respuesta los devuelve**. Así lo que se ve
	 * en local es lo mismo que se verá contra la API real (ver el TODO de
	 * `FeedbackTeamSession`).
	 */
	private feedbackTeam: FeedbackTeamRow[] = [
		{ id: 11, user_id: 88, fullname: "Sergio Pérez Fernández", avatar_name: { initials: "SP", image: null, title: "Sergio Pérez Fernández" }, criteria_label: "Puesto", criteria: "Técnico vendedor", score: { final: 87.2, calculated: 87.2, calibrated: null, max: 100, incomplete: false, competencies: 90, objectives: 83 }, approval: { code: "approved", name: "Aprobado", color: "primary", min_score: 60 }, has_feedback: false, feedback: null },
		{ id: 12, user_id: 89, fullname: "Sandra Sánchez Fernández", avatar_name: { initials: "SS", image: null, title: "Sandra Sánchez Fernández" }, criteria_label: "Puesto", criteria: "Técnico vendedor", score: { final: 74.5, calculated: 74.5, calibrated: null, max: 100, incomplete: false, competencies: 78, objectives: 71 }, approval: { code: "approved", name: "Aprobado", color: "primary", min_score: 60 }, has_feedback: true, feedback: { id: 42, title: "Feedback de cierre", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 45, session_type: "", meeting_platform: null, meeting_link: "https://meet.google.com/abc-defg-hij", address: null, acceptance_requirement: "signature", accepted: false, accepted_at: null, points_progress: { checked: 0, total: 10, percent: 0 }, status: FEEDBACK_STATUS_PENDING } },
		{ id: 13, user_id: 90, fullname: "Lucía Jiménez Rodríguez", avatar_name: { initials: "LJ", image: null, title: "Lucía Jiménez Rodríguez" }, criteria_label: "Puesto", criteria: "Técnico vendedor", score: { final: 91, calculated: 88, calibrated: 91, max: 100, incomplete: false, competencies: 93, objectives: 89 }, approval: { code: "approved", name: "Aprobado", color: "primary", min_score: 60 }, has_feedback: true, feedback: { id: 43, title: "Reunión de feedback anual", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 60, session_type: "virtual", meeting_platform: "zoom", meeting_link: "https://zoom.us/j/1234567890", address: null, acceptance_requirement: "signature", accepted: false, accepted_at: null, points_progress: { checked: 3, total: 10, percent: 30 }, status: FEEDBACK_STATUS_DONE } },
		{ id: 14, user_id: 91, fullname: "Patricia Fernández Pérez", avatar_name: { initials: "PF", image: null, title: "Patricia Fernández Pérez" }, criteria_label: "Puesto", criteria: "Analista comercial", score: { final: 52.4, calculated: 52.4, calibrated: null, max: 100, incomplete: false, competencies: 58, objectives: 47 }, approval: { code: "failed", name: "Desaprobado", color: "negative", min_score: 60 }, has_feedback: true, feedback: { id: 44, title: "Feedback de cierre", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 45, session_type: "presencial", meeting_platform: null, meeting_link: null, address: "Av. Primavera 120, Santiago de Surco, Lima", acceptance_requirement: "signature", accepted: false, accepted_at: null, points_progress: { checked: 0, total: 10, percent: 0 }, status: FEEDBACK_STATUS_SCHEDULED } },
		{ id: 15, user_id: 92, fullname: "Elena Jiménez Pérez", avatar_name: { initials: "EJ", image: null, title: "Elena Jiménez Pérez" }, criteria_label: "Puesto", criteria: "Analista comercial", score: { final: null, calculated: null, calibrated: null, max: 100, incomplete: true, competencies: null, objectives: null }, approval: { code: "pending", name: "Sin nota", color: "grey", min_score: 60 }, has_feedback: true, feedback: { id: 45, title: "Feedback de cierre", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 45, session_type: "", meeting_platform: null, meeting_link: "https://meet.google.com/klm-nopq-rst", address: null, acceptance_requirement: "check", accepted: false, accepted_at: null, points_progress: { checked: 0, total: 10, percent: 0 }, status: FEEDBACK_STATUS_SCHEDULED } },
		{ id: 16, user_id: 93, fullname: "David Jiménez García", avatar_name: { initials: "DJ", image: null, title: "David Jiménez García" }, criteria_label: "Puesto", criteria: "Técnico vendedor", score: { final: 66.8, calculated: 66.8, calibrated: null, max: 100, incomplete: false, competencies: 70, objectives: 64 }, approval: { code: "approved", name: "Aprobado", color: "primary", min_score: 60 }, has_feedback: false, feedback: null },
		{ id: 17, user_id: 94, fullname: "Ana María Martínez Gutiérrez", avatar_name: { initials: "AM", image: null, title: "Ana María Martínez Gutiérrez" }, criteria_label: "Puesto", criteria: "Técnico vendedor", score: { final: 83.1, calculated: 83.1, calibrated: null, max: 100, incomplete: false, competencies: 85, objectives: 81 }, approval: { code: "approved", name: "Aprobado", color: "primary", min_score: 60 }, has_feedback: true, feedback: { id: 46, title: "Feedback de cierre", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 45, session_type: "virtual", meeting_platform: "zoom", meeting_link: "https://zoom.us/j/9876543210", address: null, acceptance_requirement: "none", accepted: false, accepted_at: null, points_progress: { checked: 0, total: 10, percent: 0 }, status: FEEDBACK_STATUS_DONE } },
		{ id: 18, user_id: 95, fullname: "Laura García Rodríguez", avatar_name: { initials: "LG", image: null, title: "Laura García Rodríguez" }, criteria_label: "Puesto", criteria: "Analista comercial", score: { final: 58.9, calculated: 58.9, calibrated: null, max: 100, incomplete: false, competencies: 61, objectives: 57 }, approval: { code: "failed", name: "Desaprobado", color: "negative", min_score: 60 }, has_feedback: true, feedback: { id: 47, title: "Feedback de cierre", session_date: "30/10/2026 14:00", session_date_raw: "2026-10-30 14:00", duration: 45, session_type: "presencial", meeting_platform: null, meeting_link: null, address: "Av. Primavera 120, Santiago de Surco, Lima", acceptance_requirement: "signature", accepted: false, accepted_at: null, points_progress: { checked: 0, total: 10, percent: 0 }, status: FEEDBACK_STATUS_SCHEDULED } },
	];

	/**
	 * Marcas de inicio/cierre de cada sesión (`PUT …/start` y `PUT …/finish`), por
	 * id de sesión.
	 *
	 * Van aparte y no dentro de `feedbackTeam[].feedback` a propósito: esas tres
	 * llaves las devuelve el **detalle**, no el listado, y el store guarda la fila
	 * con la forma exacta del listado (`FeedbackTeamSession`).
	 */
	private feedbackSessionTimes: Record<
		string,
		{ started_at: string | null; finished_at: string | null; finished_source: string | null }
	> = {};

	/**
	 * Grabaciones de reunión subidas en esta sesión, por `reviewee_id`. `uploadedAt`
	 * solo existe en el mock: sirve para simular cuánto tarda IsA en resumir.
	 */
	private feedbackRecordings: Record<string, ApiFeedbackRecording & { uploadedAt: number }> = {};

	/** Cuánto "tarda" IsA en generar el resumen de una grabación. */
	private static readonly SUMMARY_DELAY_MS = 8000;

	/**
	 * Transcripción de ejemplo. Es lo que se dijo, **en texto plano** y sin
	 * redactar: a propósito no se parece al resumen, que es lo que permite ver que
	 * el panel muestra cada uno en su lugar.
	 */
	private static readonly FEEDBACK_TRANSCRIPT_TEXT =
		"Líder: Hola, gracias por hacerte un espacio. La idea de hoy es repasar cómo te fue en el semestre.\n" +
		"Evaluado: Perfecto, vengo con las notas de los objetivos.\n" +
		"Líder: Empecemos por lo de la propuesta de mejora del flujo del equipo, que salió muy bien: bajamos como quince por ciento los tiempos de entrega.\n" +
		"Evaluado: Sí, ahí ayudó mucho haber ordenado los pases entre turnos.\n" +
		"Líder: Y cerraste el cien por ciento de los objetivos del trimestre. Donde vamos a poner el foco es en satisfacción interna del equipo, que quedó más abajo.\n" +
		"Evaluado: De acuerdo. Se me ocurre arrancar con las visitas mensuales a los clientes clave y una encuesta al equipo cada tres meses.\n" +
		"Líder: Me parece. Lo dejamos como acuerdo y lo revisamos en la próxima.";

	/** Resumen de ejemplo que devuelve IsA (el backend real lo envía en HTML). */
	private static readonly FEEDBACK_SUMMARY_HTML = `
		<p>Demostró iniciativa al proponer mejoras en el flujo de trabajo del equipo, lo cual
		redujo tiempos de entrega en un 15%.</p>
		<p>Cumplió con el 100% de los objetivos trimestrales asignados, destacando la calidad
		en cada entregable presentado.</p>
		<p>Mostró liderazgo al coordinar al equipo durante la ausencia del supervisor,
		manteniendo la productividad y el ambiente laboral.</p>
		<p>Se capacitó de forma autónoma en herramientas de análisis de datos, aplicando los
		conocimientos adquiridos en el proyecto actual.</p>
	`;

	/**
	 * El año, las evaluaciones emitidas y el cierre del proceso que `GET
	 * …/feedbacks/{id}/show` devuelve dentro de `feedback` (el puesto va aparte,
	 * como `position`, desde el criterio de la fila).
	 */
	private feedbackDetailExtras = {
		year: 2026,
		evaluations: { done: 2, total: 2 },
		close_date: "2026-12-14",
	};

	/**
	 * `data.results` del detalle-guion, en la **forma exacta del backend**: llega
	 * como hermano de `feedback`, con `score` reutilizando el mismo objeto que la
	 * fila del listado.
	 *
	 * Las notas de las competencias van en la escala `0`–`max` (100), no en
	 * niveles, y traen el desglose `manager` / `self`: la última fila deja a
	 * propósito una brecha grande entre lo que puso el líder y la autoevaluación,
	 * que es el caso que la vista tiene que hacer evidente.
	 *
	 * Los objetivos incluyen filas **con y sin `percent`**: contra la API real hoy
	 * llega siempre `null`, así que sin una fila poblada no se podría previsualizar
	 * el chip ni la barra de cumplimiento.
	 */
	private feedbackDetailResults = {
		objectives: {
			total: 5,
			achieved: 3,
			list: [
				{ id: 1, name: "Incremento de ventas", weight: null, target: "15.00", achieved: "18.00", percent: 120, approved: true, status: FEEDBACK_OBJECTIVE_STATUS_APPROVED },
				{ id: 2, name: "Retención de clientes", weight: null, target: "90.00", achieved: "88.00", percent: 88, approved: true, status: FEEDBACK_OBJECTIVE_STATUS_APPROVED },
				{ id: 3, name: "Reportes entregados a tiempo", weight: null, target: "95.00", achieved: "92.00", percent: 97, approved: true, status: FEEDBACK_OBJECTIVE_STATUS_APPROVED },
				{ id: 4, name: "Satisfacción interna del equipo", weight: null, target: "80.00", achieved: "60.00", percent: 75, approved: true, status: FEEDBACK_OBJECTIVE_STATUS_APPROVED },
				// Sin medir: así se ve la fila que hoy devuelve la API real.
				{ id: 5, name: "Metas trimestrales del módulo", weight: null, target: "85.00", achieved: null, percent: null, approved: true, status: FEEDBACK_OBJECTIVE_STATUS_APPROVED },
			],
		},
		competencies: {
			max: 100,
			list: [
				{ id: 19, name: "Trabajo en equipo", manager: 100, self: 100, value: 100 },
				{ id: 20, name: "Comunicación efectiva", manager: 75, self: 100, value: 87.5 },
				{ id: 21, name: "Orientación de resultados", manager: 75, self: 75, value: 75 },
				{ id: 22, name: "Adaptabilidad al cambio", manager: 50, self: 50, value: 50 },
				{ id: 23, name: "Liderazgo e iniciativa", manager: 10, self: 100, value: 55 },
			],
		},
	};

	/**
	 * La guía que "propone ISA" a partir de los resultados de la persona: es lo que
	 * devuelve `POST …/feedbacks/points/preview` y la semilla de una sesión nueva.
	 *
	 * Los `id` van en `null` porque todavía no están guardados — igual que en el
	 * contrato real, donde el punto recién nace al hacer el `store` o el
	 * `PUT …/points`.
	 */
	private static readonly FEEDBACK_GUIDE_TEMPLATE: FeedbackBlock[] = [
		{
			block_code: "opening",
			title: "Apertura",
			tone: "recognition",
			notes: null,
			points: [
				{ id: null as any, text: "Abrir felicitando el logro en incremento de ventas", detail: "Meta +15% → logrado 18% (120% de cumplimiento)", source: "isa", checked: false, checked_at: null },
			],
		},
		{
			block_code: "objectives",
			title: "Objetivos — 87/100",
			tone: "strength",
			notes: null,
			points: [
				{ id: null as any, text: "Reconocer los reportes entregados a tiempo", detail: "97% de cumplimiento, muy cerca del 100%", source: "isa", checked: false, checked_at: null },
			],
		},
		{
			block_code: "objectives-attention",
			title: "Objetivos a conversar",
			tone: "attention",
			notes: null,
			points: [
				{ id: null as any, text: "Retención de clientes: preguntar qué dificultades encontró", detail: "Meta 90% → logrado 88%", source: "isa", checked: false, checked_at: null },
				{ id: null as any, text: "Satisfacción interna del equipo: es el más bajo, indagar causas", detail: "75% — objetivo con mayor brecha", source: "isa", checked: false, checked_at: null },
				{ id: null as any, text: "Definir en conjunto un plan de acción concreto para este punto", detail: null, source: "isa", checked: false, checked_at: null },
			],
		},
		{
			block_code: "competencies",
			title: "Competencias — 84/100",
			tone: "strength",
			notes: null,
			points: [
				{ id: null as any, text: "Destacar comunicación efectiva y orientación a resultados como puntos fuertes", detail: null, source: "isa", checked: false, checked_at: null },
				{ id: null as any, text: "Conversar sobre adaptabilidad al cambio: espacio de crecimiento", detail: null, source: "isa", checked: false, checked_at: null },
				{ id: null as any, text: "Conversar sobre liderazgo e iniciativa: espacio de crecimiento", detail: null, source: "isa", checked: false, checked_at: null },
			],
		},
		{
			block_code: "closing",
			title: "Cierre",
			tone: "recognition",
			notes: null,
			points: [
				{ id: null as any, text: "Resumir en voz alta los acuerdos tomados en la reunión", detail: null, source: "isa", checked: false, checked_at: null },
				{ id: null as any, text: "Definir fecha de seguimiento para revisar avances", detail: null, source: "isa", checked: false, checked_at: null },
			],
		},
	];

	/** Guía guardada de cada sesión, por `feedback_id`. */
	private feedbackGuides: Record<string, FeedbackBlock[]> = {};

	/** Autoincremental de ids de punto, para que cada punto guardado tenga el suyo. */
	private feedbackPointSeq = 900;

	/** Autoincremental de ids de sesión, para las reuniones que se agendan en vivo. */
	private feedbackSessionSeq = 47;

	// ---- Feedback (vista del colaborador: "Mi feedback") -------------------

	/**
	 * Sesión de feedback del evaluado, en la **forma exacta del backend**
	 * (`GET …/me/processes/{id}/feedback`, snake_case dentro de `{ data }`). Es lo
	 * mismo que lee la vista: no hay traducción de por medio.
	 *
	 * El `status` lo recalcula {@link getMyFeedback} comparando el **fin** de la
	 * reunión (`session_date_raw` + `duration`) con la hora actual: si todavía no
	 * termina responde "Agendada" (la vista muestra el detalle de la cita); una vez
	 * terminada, "Completada". `results_available`, `results` y `blocks` también
	 * los arma {@link getMyFeedback} con la regla del backend (completada y, si pide
	 * conformidad, aceptada). Así los estados se previsualizan moviendo esas llaves
	 * — y con la reunión **en curso** se ve el otro caso: sigue agendada aunque su
	 * hora de inicio ya haya pasado.
	 *
	 * Para previsualizar el estado **"sin reunión agendada"**, poner `feedback: null`.
	 */
	private myFeedback: { feedback: any; signature: any; signature_config: any } = {
		feedback: {
			id: 44,
			title: "Feedback de cierre",
			reviewer_fullname: "Carlos Vega",
			session_date: "01/01/2026 13:15",
			session_date_raw: "2026-01-01 13:15",
			duration: 45,
			// Modalidad: "presencial" ⇒ LUGAR (usa `address`), "virtual" ⇒ Zoom y
			// vacío/null ⇒ enlace libre (ambos usan `meeting_link`).
			session_type: "virtual",
			// El backend lo envía siempre vacío; el front no lo mira.
			meeting_platform: null,
			meeting_link: "https://zoom.us/j/1234567890",
			address: "Av. Javier Prado Oeste 2345 — Edificio Renault, Dpt. 234",
			latitude: -12.0908,
			longitude: -77.0503,
			agenda: "Cierre del semestre",
			comments: "<p>Buen semestre, con foco en satisfacción del equipo.</p>",
			action_items: [
				{ id: 1, description: "Plan de visitas mensuales a clientes clave", due_date: "2026-11-30", source: "manual", completed_at: null },
				{ id: 2, description: "Encuesta interna trimestral de satisfacción del equipo", due_date: null, source: "manual", completed_at: "2026-10-02T10:00:00-05:00" },
			],
			status: FEEDBACK_STATUS_SCHEDULED,
			// Arranca pendiente de firma para que la conformidad se vea al entrar.
			// Poner `"none"` para previsualizar la vista sin conformidad.
			acceptance_requirement: "signature",
			accepted: false,
			accepted_at: null,
			acceptance_type: null,
			signature_name: null,
			acceptance_comment: null,
			// cabecera del evaluado (`ProcessRevieweeHeader::forFeedback`)
			position: "Técnico vendedor",
			year: 2026,
			close_date: "2026-12-14",
			evaluations: { done: 2, total: 2 },
		},
		signature: null,
		signature_config: { min_strokes: 2, min_duration_ms: 400 },
	};

	// ---- Autoevaluación de competencias ------------------------------------

	/** Colores por posición de la escala de 4 niveles (Bajo → Excelente). */
	private static readonly SCALE_COLORS = ["#e11d48", "#f59e0b", "#4f46e5", "#059669"];

	/**
	 * Construye una escala de 4 niveles (Bajo/Regular/Bueno/Excelente) con la
	 * descripción propia de cada nivel para la competencia dada. El backend real
	 * envía la escala por competencia (cada nivel con su `description`), así que
	 * cada competencia del mock arma la suya con {@link makeScale}.
	 */
	private static makeScale(
		baseId: number,
		descriptions: [string, string, string, string],
	): any {
		const names = ["Bajo", "Regular", "Bueno", "Excelente"];
		const values = ["10.00", "50.00", "75.00", "100.00"];
		return {
			id: 25,
			code: "four-levels",
			name: "Escala de 4 niveles",
			color: null,
			values: names.map((name, i) => ({
				id: baseId + i,
				name,
				color: PerformanceMockDb.SCALE_COLORS[i],
				position: i + 1,
				value: values[i],
				description: descriptions[i],
			})),
		};
	}

	/** Competencias a autoevaluar (forma cruda de `me/processes/:id/competencies`). */
	private selfAssessmentCompetencies = [
		{
			id: 1,
			competency_id: 1,
			name: "Puntualidad",
			description:
				'<p>Cumple con los horarios establecidos, llega a tiempo a su turno y actividades programadas. Además, mantiene su presentación personal según lineamientos, proyectando una imagen profesional y acorde a la marca.</p>',
			scale_type: PerformanceMockDb.makeScale(35, [
				"No llega a tiempo y su presentación no es adecuada para la tienda.",
				"A veces llega tarde o no cumple al 100% con el uniforme o presentación personal.",
				"Casi siempre llega a tiempo y suele mantener una imagen alineada con la marca y los protocolos.",
				"Siempre llega a tiempo y mantiene una imagen alineada con la marca y los protocolos.",
			]),
		},
		{
			id: 2,
			competency_id: 2,
			name: "Respuesta ágil ante urgencias",
			description:
				'<p>Reacciona con rapidez y eficiencia ante situaciones urgentes, detecta a tiempo los problemas y da soluciones siguiendo el lineamiento marcado.</p>',
			scale_type: PerformanceMockDb.makeScale(39, [
				"No reacciona a tiempo ante situaciones urgentes.",
				"A veces actúa con rapidez pero le cuesta, o se le tiene que pedir con mucha insistencia.",
				"Casi siempre actúa rápidamente y resuelve la situación con efectividad.",
				"Siempre actúa con agilidad ante temas urgentes para resolverlos según lineamientos.",
			]),
		},
		{
			id: 3,
			competency_id: 3,
			name: "Dirección del equipo",
			description:
				'<p>Organiza al equipo, da dirección y guía para cumplir indicadores. Comunica claramente los lineamientos validando que el equipo los entienda y los cumpla.</p>',
			scale_type: PerformanceMockDb.makeScale(43, [
				"No da dirección ni seguimiento al equipo.",
				"A veces no es claro en las indicaciones y no da seguimiento.",
				"Casi siempre es claro en las indicaciones y organiza al equipo.",
				"Siempre da indicaciones con claridad y organiza al equipo para cumplir los indicadores.",
			]),
		},
		{
			id: 4,
			competency_id: 4,
			name: "Escucha y clima laboral",
			description:
				'<p>Escucha activamente, interviene cuando hay tensiones y cuida el clima del equipo.</p>',
			scale_type: PerformanceMockDb.makeScale(47, [
				"No promueve un buen clima en el equipo.",
				"A veces escucha pero evita intervenir en las tensiones.",
				"Casi siempre se da un tiempo para escuchar al equipo.",
				"Siempre se preocupa por escuchar al equipo y resolver conflictos.",
			]),
		},
	];

	// ===== API del store =====================================================

	listProcesses(): any {
    // ProcessSummary[]
		return {data: {
        data: this.processSummaries
      }
    };
	}

	/** Data del Home del módulo de desempeño (envoltura `{ data }` de la API real). */
	getPerformanceHome(): PerformanceHomeResponse {
		return this.performanceHome;
	}

	/**
	 * Resumen GLOBAL del usuario para el Home (`GET .../profile`): datos básicos,
	 * indicadores, siguiente actividad y organización (líder + equipo). Forma cruda
	 * de la API real.
	 *
	 * `next_activity`: la documentación la trae `null` y no especifica su forma;
	 * el mock la puebla con el contrato ESPERADO (best-effort) para poder demostrar
	 * la tarjeta de actividad en curso. Confirmar campos con backend.
	 */
	getProfile(): PerformanceProfileResponse {
		return {
			data: {
				user: { image: null, initials: "LP", title: "Liliana Pachari", subtitle: null },
				workspace: { id: 1, name: "Koepp Ltd", slug: "koepp-ltd" },
				subworkspace: { id: 2, name: "Gibson PLC", slug: "gibson-plc-2" },
				stats: {
					days_remaining: 12,
					team_review_percentage: 85,
					requested_changes_count: 14,
				},
				next_activity: {
					title: "Evaluando a mi jefe",
					status: { name: "EN CURSO" },
					days_remaining: "5 días restantes",
					due_date: "Vence el 30 de Junio",
					progress_percentage: 60,
					action_label: "Continuar ahora",
				},
				organization: {
					leader: { image: null, initials: "CR", title: "Carlos Ramos", subtitle: null },
					team: {
						active_participants_count: 10,
						members_preview: [
							{ image: null, initials: "AT", title: "Ana Torres", subtitle: null },
							{ image: null, initials: "LV", title: "Luis Vega", subtitle: null },
							{ image: null, initials: "MD", title: "María Díaz", subtitle: null },
							{ image: null, initials: "JS", title: "Jorge Salas", subtitle: null },
						],
					},
				},
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Procesos recientes del usuario con su avance por etapa (`GET
	 * .../processes/latest?limit=&active=`). Forma cruda de la API real; el mock
	 * recorta al `limit` pedido (el backend define el default = 3). `active`
	 * (C4, design.md §4.12) no cambia nada hoy: los tres procesos del mock están
	 * todos activos (ninguno trae `status` culminado); se acepta el parámetro
	 * para que `PerformanceRealtimeService.getLatestProcesses(50, {active:true})`
	 * no rompa contra el mock.
	 */
	getLatestProcesses(limit?: number | string | null, _active?: boolean): LatestProcessesResponse {
		const buildStages = (currentCode: string) => {
			const catalog = [
				{ id: 113, code: "competencies", name: "Competencias" },
				{ id: 50, code: "objectives", name: "Objetivos" },
				{ id: 51, code: "tracking", name: "Seguimiento" },
				{ id: 52, code: "evaluation", name: "Evaluación" },
				{ id: 114, code: "evaluation_objectives", name: "Evaluación de Objetivos" },
				{ id: 53, code: "calibration", name: "Calibración" },
				{ id: 54, code: "feedback", name: "Feedback" },
				{ id: 55, code: "pdi", name: "Plan de Desarrollo Individual" },
			];
			const currentIndex = catalog.findIndex((stage) => stage.code === currentCode);
			return catalog.map((stage, index) => ({
				...stage,
				position: index + 1,
				status: (index < currentIndex
					? "completed"
					: index === currentIndex
						? "current"
						: "locked") as "completed" | "current" | "locked",
			}));
		};

		// Las banderas de rol se varían entre procesos a propósito: son las que
		// ejercitan el filtro de etapas del Home (un evaluado puro no ve las etapas
		// que solo tiene el líder, como "Calibración").
		const processes = [
			{
				id: 7,
				name: "Evaluación desempeño asesoras y almaceneros",
				current_stage: { id: 50, code: "objectives", name: "Objetivos" },
				stages: buildStages("objectives"),
				is_reviewee: true,
				is_reviewer: true,
				roles: ["reviewee", "reviewer"],
			},
			{
				id: 1,
				name: "Evaluación desempeño líderes Administradores - Zona Lourdes",
				current_stage: { id: 51, code: "tracking", name: "Seguimiento" },
				stages: buildStages("tracking"),
				is_reviewee: true,
				is_reviewer: false,
				roles: ["reviewee"],
			},
			{
				id: 3,
				name: "Evaluación desempeño anual 2026",
				current_stage: { id: 54, code: "feedback", name: "Feedback" },
				stages: buildStages("feedback"),
				is_reviewee: false,
				is_reviewer: true,
				roles: ["reviewer"],
			},
		];

		const max = Number(limit) > 0 ? Number(limit) : 3;
		return {
			data: processes.slice(0, max),
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Anuncios ("Novedades") del Home (`GET .../announcements`), paginados igual que
	 * la API real (envoltura `{ data: { current_page, data: [...], ... } }`). La
	 * `image` va como URL ABSOLUTA. El mock devuelve una sola página fija.
	 */
	getAnnouncements(page?: number | string | null, perPage?: number | string | null): PerformanceAnnouncementsResponse {
		const items = [
			{
				id: 101,
				title: "¡Bienvenido a tu campus digital!",
				description:
					"<p>En Campus Digital nos preocupamos por tu bienestar integral, es por eso que tenemos para ti un seguro de vida y salud.</p>",
				image: "https://cdn.example.com/announcements/101.jpg",
				link: null,
				published_at: "2026-06-01T09:00:00.000Z",
				end_date: "2026-07-01T09:00:00.000Z",
			},
			{
				id: 102,
				title: "Bienvenido a tu nuevo entorno de aprendizaje 2026",
				description:
					"<p>Hemos actualizado nuestra plataforma para brindarte una experiencia más rápida e intuitiva. Explora los nuevos cursos disponibles.</p>",
				image: "https://cdn.example.com/announcements/102.jpg",
				link: null,
				published_at: "2026-05-20T09:00:00.000Z",
				end_date: null,
			},
		];
		const currentPage = Number(page) > 0 ? Number(page) : 1;
		const size = Number(perPage) > 0 ? Number(perPage) : 15;
		return {
			data: {
				current_page: currentPage,
				data: items,
				last_page: 1,
				per_page: size,
				total: items.length,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	getProcess(_processId?: number | string): PerformanceProcessResponse {
		return { data: { ...this.process, id: _processId} };
	}

	/**
	 * Análisis de avance (ISA) del proceso, con la envoltura de la API real. Si el
	 * body trae `stage_id`, devuelve el análisis de esa etapa (resuelto contra las
	 * etapas del proceso mock); si no, el general. `generated` sale en `false`
	 * porque el mock no genera nada con IA: siempre devuelve el mismo registro.
	 */
	getIsaAnalysis(processId?: number | string, body: IsaAnalysisRequest = {}): IsaAnalysisResponse {
		return this.buildIsaAnalysis(processId, body);
	}

	/**
	 * Análisis de avance (ISA) de un evaluado a cargo del líder. El backend real lo
	 * redacta en tercera persona ("Juan va al día…") y valida que el evaluado
	 * pertenezca al evaluador (403 si no); el mock reutiliza el mismo texto por
	 * etapa y solo refleja el `reviewee_id` recibido.
	 */
	getTeamIsaAnalysis(processId?: number | string, body: IsaTeamAnalysisRequest = { reviewee_id: 88 }): IsaAnalysisResponse {
		return this.buildIsaAnalysis(processId, body, body?.reviewee_id);
	}

	private buildIsaAnalysis(
		processId?: number | string,
		body: IsaAnalysisRequest | IsaTeamAnalysisRequest = {},
		revieweeId?: number | string,
	): IsaAnalysisResponse {
		const stageCode = body?.stage_id
			? this.process.stages?.find((stage) => String(stage.stage?.id) === String(body.stage_id))?.stage?.code
			: null;

		const entry = (stageCode && this.isaAnalysisByStage[stageCode]) || this.isaAnalysisByStage["general"];
		const scope = stageCode ? "stage" : "general";

		return {
			data: {
				record: {
					_id: `mock-isa-${scope}-${stageCode ?? "all"}-${revieweeId ?? "me"}`,
					process_reviewee_id: 45,
					reviewee_id: Number(revieweeId) || 88,
					process_id: Number(processId) || 1,
					workspace_id: 1,
					scope,
					stage_id: body?.stage_id ? Number(body.stage_id) : null,
					stage_code: entry.stage_code,
					stage_name: entry.stage_name,
					generated_on: new Date().toISOString().slice(0, 10),
					forced: !!body?.forced,
					metrics: entry.metrics,
					analysis: entry.analysis,
					created_at: new Date().toISOString(),
					updated_at: new Date().toISOString(),
				},
				generated: false,
				finished: false,
				scope,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Objetivos del proceso. `memberId` se acepta para reflejar el endpoint real
	 * (plan de un colaborador) pero el mock devuelve siempre la misma lista, igual
	 * que hace hoy la app (las vistas "mis objetivos" y "plan del equipo" comparten lista).
	 */

  listPersonalObjectives(_processId?: number | string, _code?: string | null): any {
		// El backend real responde paginado y envuelto en `{ data: { data: [...] } }`.
		// Para probar el estado vacío de la vista, dejar `items` en [] (o vaciar `myGoals`).
		// `code=evaluation` pide solo los objetivos evaluables de la etapa de revisión;
		// el mock devuelve la misma lista (el backend real aplica el filtro).
		const items = this.myGoals.map((g: any) => this.withMetricChildren(g));
		return {
			data: {
				current_page: 1,
				data: items,
				first_page_url: null,
				from: items.length ? 1 : null,
				last_page: 1,
				last_page_url: null,
				links: [],
				next_page_url: null,
				path: "",
				per_page: 15,
				prev_page_url: null,
				to: items.length || null,
				total: items.length,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Adjunta los marcadores hijos de la métrica (desde la taxonomía) si el objetivo
	 * trae la métrica sin `children`, para que el formulario pueda reconstruir las
	 * filas de escala al editar.
	 */
	private withMetricChildren(goal: any): any {
		const metric = goal?.measurement_metric;
		if (!metric || (metric.children && metric.children.length)) return goal;
		const catalog = this.objectiveFormData.measurement_metrics.find(
			(m) => String(m.id) === String(metric.id)
		);
		if (!catalog) return goal;
		return { ...goal, measurement_metric: { ...metric, children: catalog.children } };
	}

	/** Taxonomías del formulario de objetivo (envoltura `{ data }` de la API real). */
	getObjectiveFormData(): any {
		return {
			data: this.objectiveFormData,
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Sugerencia de objetivo generada por IA (ISA). El backend la arma a partir del
	 * proceso, el perfil del usuario y sus objetivos; el mock devuelve siempre la
	 * misma sugerencia con la envoltura `{ data }` de la API real. Las taxonomías
	 * (ids de unidad/métrica/tipo y `measurement_metric_values`) corresponden a las
	 * de {@link objectiveFormData} para que el formulario las resuelva al precargar.
	 */
	getObjectiveIsaSuggestion(): any {
		return {
			data: {
				name: "Mejorar la satisfacción del equipo en Zona Lourdes",
				description:
					"Liliana Cursalab debe aumentar la satisfacción del equipo a cargo en la Zona Lourdes, logrando al menos un 85% de satisfacción promedio durante este proceso de evaluación. Esta mejora contribuirá a un mejor clima laboral y mayor productividad del equipo de líderes administradores. El objetivo se medirá mediante encuestas específicas al final del periodo de evaluación.",
				initiatives: [
					{
						name: "Implementar reuniones quincenales",
						description:
							"Organizar reuniones cada dos semanas para recoger feedback y resolver inquietudes del equipo.",
					},
					{
						name: "Realizar encuestas de satisfacción",
						description:
							"Diseñar y aplicar encuestas mensuales para medir la percepción y bienestar del equipo.",
					},
					{
						name: "Desarrollar plan de acción para clima",
						description:
							"Crear y ejecutar un plan basado en resultados de encuestas para mejorar el ambiente laboral.",
					},
				],
				smart_breakdown: {
					specific:
						"El objetivo es aumentar la satisfacción del equipo de líderes administradores en la Zona Lourdes.",
					measurable:
						"Se mide con una encuesta que debe alcanzar un promedio mínimo del 85% de satisfacción.",
					achievable:
						"El rango de 70% mínimo es realista dado su rol y herramientas disponibles.",
					relevant:
						"Mejorar la satisfacción contribuye al desempeño y clima del equipo evaluado en este proceso.",
					time_bound: "El objetivo se cumple dentro del periodo de esta evaluación de desempeño.",
				},
				measurement_unit_id: 40,
				evaluation_type_id: 19,
				measurement_metric_id: 17,
				measurement_value: 85,
				// Reales, en la unidad de la meta, como los devuelve la API: Isa decide el
				// 70 %, 85 % y 95 % de la meta y el backend los convierte (entero con meta > 10).
				measurement_metric_values: [
					{ taxonomy_id: 27, value: 60 },
					{ taxonomy_id: 28, value: 72 },
					{ taxonomy_id: 29, value: 81 },
				],
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Sugerencia de objetivo generada por IA (ISA) para un evaluado (vista del
	 * líder). El backend la arma a partir del `reviewee_id` (requerido), su perfil
	 * y objetivos existentes; el mock devuelve siempre la misma sugerencia con la
	 * envoltura `{ data }` de la API real. Las taxonomías (ids de unidad/métrica/
	 * tipo y `measurement_metric_values`) corresponden a las de
	 * {@link objectiveFormData} para que el formulario las resuelva al precargar.
	 */
	getTeamObjectiveIsaSuggestion(_dto?: any): any {
		return {
			data: {
				name: "Incrementar ventas mensuales de productos de belleza en Larco",
				description:
					"Mar Rosario debe aumentar las ventas mensuales de productos de belleza en la tienda Larco en un 15% durante este periodo de evaluación para contribuir a los objetivos comerciales de la zona Lourdes. Este aumento mejorará la rentabilidad de la tienda y demostrará su efectividad como Asesor(a) de Belleza. Alcanzar esta meta fortalecerá la satisfacción del cliente al ofrecer productos adecuados y mejorar la experiencia de compra.",
				initiatives: [
					{
						name: "Capacitación en técnicas de venta",
						description:
							"Participar en sesiones de formación sobre técnicas de venta y conocimiento profundo de productos para potenciar la asesoría al cliente, programadas semanalmente durante el periodo.",
					},
					{
						name: "Promociones dirigidas al cliente",
						description:
							"Diseñar y ejecutar promociones mensuales enfocadas en productos de alta rotación para incentivar la compra y alcanzar objetivos de ventas.",
					},
					{
						name: "Seguimiento post-venta",
						description:
							"Realizar seguimiento personalizado a clientes clave para fomentar la recompra y la fidelización durante el trimestre.",
					},
				],
				smart_breakdown: {
					specific: "Define aumentar ventas mensuales de belleza en tienda Larco, responsabilidad directa de Mar Rosario.",
					measurable: "Objetivo cuantificado con un aumento del 15% en ventas mensuales medido en unidades vendidas.",
					achievable: "El 15% de incremento es alcanzable con capacitación y promociones adecuadas en el puesto.",
					relevant: "El incremento de ventas contribuye directamente a metas comerciales de la zona Lourdes y rol de asesor(a).",
					time_bound: "El objetivo debe cumplirse durante el periodo de evaluación vigente del proceso.",
				},
				measurement_unit_id: 41,
				evaluation_type_id: 19,
				measurement_metric_id: 17,
				measurement_value: 115,
				// Reales, en la unidad de la meta, como los devuelve la API. El 105/110/115 %
				// que proponía no respeta los 10 puntos entre marcadores: el backend lo
				// reemplaza por el reparto parejo 70/95/120 % y lo convierte con la meta.
				measurement_metric_values: [
					{ taxonomy_id: 27, value: 81 },
					{ taxonomy_id: 28, value: 109 },
					{ taxonomy_id: 29, value: 138 },
				],
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Objetivos del plan de un integrante del equipo (forma cruda paginada del
	 * endpoint real `GET .../team/processes/:id/objectives`). El backend filtra por
	 * `reviewee_id`; el mock devuelve siempre el mismo plan (convención del módulo)
	 * y pagina por `page` (el backend define el `per_page`).
	 */
	listTeamMemberObjectives(
		_processId?: number | string,
		_revieweeId?: number | string | null,
		page?: number | string | null,
	): any {
		const items = this.teamObjectives;
		const perPage = 15;
		const total = items.length;
		const lastPage = Math.max(1, Math.ceil(total / perPage));
		const currentPage = Math.min(Math.max(1, Number(page) || 1), lastPage);
		const start = (currentPage - 1) * perPage;
		const pageItems = items.slice(start, start + perPage);

		return {
			data: {
				current_page: currentPage,
				data: pageItems,
				first_page_url: null,
				from: total ? start + 1 : null,
				last_page: lastPage,
				last_page_url: null,
				links: [],
				next_page_url: currentPage < lastPage ? `?page=${currentPage + 1}` : null,
				path: "",
				per_page: perPage,
				prev_page_url: currentPage > 1 ? `?page=${currentPage - 1}` : null,
				to: total ? start + pageItems.length : null,
				total,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Crea un objetivo en el plan de un evaluado (endpoint real
	 * `POST .../team/processes/:id/objectives/store`). Arma la forma cruda del GET
	 * (con `objective` anidado, taxonomías resueltas y `status` "sent") a partir del
	 * cuerpo del POST, lo agrega al inicio del plan y lo devuelve envuelto en `{ data }`.
	 */
	addTeamObjective(dto: any): any {
		const id = this.nextGoalId++;
		const unit = this.resolveTaxonomy(this.objectiveFormData.measurement_units, dto.measurement_unit_id);
		const metricCatalog = this.objectiveFormData.measurement_metrics.find(
			(m) => String(m.id) === String(dto.measurement_metric_id),
		);
		const metric = metricCatalog
			? { id: metricCatalog.id, code: metricCatalog.code, name: metricCatalog.name, children: metricCatalog.children }
			: this.resolveTaxonomy(this.objectiveFormData.measurement_metrics, dto.measurement_metric_id);
		const evaluation = this.resolveTaxonomy(this.objectiveFormData.evaluation_types, dto.evaluation_type_id);
		const now = new Date().toISOString();

		const created = {
			id,
			process_reviewee_id: null,
			process_id: 1,
			reviewee_id: dto.reviewee_id ?? null,
			status_id: 89,
			objective_id: id,
			objective_weight: dto.objective_weight ?? null,
			measurement_unit_id: dto.measurement_unit_id ?? null,
			measurement_value: dto.measurement_value ?? null,
			measurement_metric_id: dto.measurement_metric_id ?? null,
			measurement_metric_values: dto.measurement_metric_values ?? [],
			initiatives: dto.initiatives ?? [],
			initiatives_count: null,
			description: dto.description ?? "",
			starts_at: dto.starts_at ?? null,
			finishes_at: dto.finishes_at ?? null,
			approved: 0,
			approved_at: null,
			created_at: now,
			updated_at: now,
			deleted_at: null,
			objective: {
				id,
				name: dto.name ?? "",
				description: dto.description ?? "",
				evaluation_type_id: dto.evaluation_type_id ?? null,
				evaluation_type: evaluation,
			},
			measurement_unit: unit,
			measurement_metric: metric,
			reviewee: { id: dto.reviewee_id ?? null, name: "", lastname: "", surname: "", fullname: "", document: null, email: null },
			status: { id: 89, name: "Pendiente de revisión", code: "sent", color: "amber-14" },
		};
		this.teamObjectives.unshift(created);

		return { data: created, message: "Objetivo creado correctamente.", type: "success", http_code: 200 };
	}

	/**
	 * Actualiza un objetivo del plan de un evaluado (endpoint real
	 * `PUT .../team/processes/:id/objectives/:objectiveId/update`). Recalcula las
	 * taxonomías resueltas (unidad/métrica/tipo) igual que {@link addTeamObjective}
	 * y conserva el `id`/`reviewee_id`. Devuelve la envoltura `{ data, message, type,
	 * http_code }` de la API real (la API real solo confirma `id`, `name` y
	 * `updated_at`, aunque el objetivo completo queda actualizado en el plan).
	 */
	updateTeamObjective(objectiveId: number | string, dto: any): any {
		const index = this.teamObjectives.findIndex((g: any) => String(g.id) === String(objectiveId));
		if (index === -1) return { data: null, message: "Objetivo no encontrado.", type: "error", http_code: 404 };

		const existing = this.teamObjectives[index];
		const unit = this.resolveTaxonomy(this.objectiveFormData.measurement_units, dto.measurement_unit_id);
		const metricCatalog = this.objectiveFormData.measurement_metrics.find(
			(m) => String(m.id) === String(dto.measurement_metric_id),
		);
		const metric = metricCatalog
			? { id: metricCatalog.id, code: metricCatalog.code, name: metricCatalog.name, children: metricCatalog.children }
			: this.resolveTaxonomy(this.objectiveFormData.measurement_metrics, dto.measurement_metric_id);
		const evaluation = this.resolveTaxonomy(this.objectiveFormData.evaluation_types, dto.evaluation_type_id);
		const now = new Date().toISOString();

		const updated = {
			...existing,
			reviewee_id: dto.reviewee_id ?? existing.reviewee_id,
			measurement_unit_id: dto.measurement_unit_id ?? existing.measurement_unit_id,
			measurement_value: dto.measurement_value ?? existing.measurement_value,
			measurement_metric_id: dto.measurement_metric_id ?? existing.measurement_metric_id,
			measurement_metric_values: dto.measurement_metric_values ?? existing.measurement_metric_values,
			initiatives: dto.initiatives ?? existing.initiatives,
			description: dto.description ?? existing.description,
			starts_at: dto.starts_at ?? existing.starts_at,
			finishes_at: dto.finishes_at ?? existing.finishes_at,
			updated_at: now,
			objective: {
				...existing.objective,
				name: dto.name ?? existing.objective?.name ?? "",
				description: dto.description ?? existing.objective?.description ?? "",
				evaluation_type_id: dto.evaluation_type_id ?? existing.objective?.evaluation_type_id ?? null,
				evaluation_type: evaluation,
			},
			measurement_unit: unit,
			measurement_metric: metric,
		};
		this.teamObjectives[index] = updated;

		return {
			data: { id: updated.id, name: updated.objective.name, updated_at: now },
			message: "Objetivo actualizado correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Elimina un objetivo del plan de un evaluado (endpoint real
	 * `DELETE .../team/processes/:id/objectives/:objectiveId/delete?reviewee_id=`).
	 * Devuelve la envoltura `{ message, type, http_code }` de la API real (sin `data`).
	 */
	deleteTeamObjective(objectiveId: number | string): any {
		const before = this.teamObjectives.length;
		this.teamObjectives = this.teamObjectives.filter((g: any) => String(g.id) !== String(objectiveId));
		const deleted = this.teamObjectives.length < before;
		return deleted
			? { message: "Objetivo eliminado correctamente.", type: "success", http_code: 200 }
			: { message: "Objetivo no encontrado.", type: "error", http_code: 404 };
	}

	/**
	 * Aprueba (o retira la aprobación de) un objetivo del plan de un evaluado
	 * (endpoint real `POST .../team/processes/:id/objectives/:objectiveId/approve`).
	 * Actualiza el estado interno del objetivo (`status`, `approved`, `approved_at`)
	 * para que el listado lo refleje al recargar, y devuelve la envoltura
	 * `{ data, message, type, http_code }` de la API real (donde `data.status` viene
	 * como `{ name, slug }`).
	 */
	approveTeamObjective(objectiveId: number | string, dto: any): any {
		const index = this.teamObjectives.findIndex((g: any) => String(g.id) === String(objectiveId));
		if (index === -1) return { data: null, message: "Objetivo no encontrado.", type: "error", http_code: 404 };

		const approved = dto?.approved !== false;
		const now = new Date().toISOString();
		const existing = this.teamObjectives[index];
		const status = approved
			? { id: 90, name: "Aprobado", code: "approved", color: "green-14" }
			: { id: 89, name: "Pendiente de revisión", code: "sent", color: "amber-14" };

		const updated = {
			...existing,
			status_id: status.id,
			status,
			approved: approved ? 1 : 0,
			approved_at: approved ? now : null,
			updated_at: now,
		};
		this.teamObjectives[index] = updated;

		return {
			data: {
				id: updated.id,
				approved,
				approved_at: updated.approved_at,
				status: { name: status.name, slug: status.code },
			},
			message: approved ? "Objetivo aprobado correctamente." : "Aprobación retirada correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Aprueba (o retira la aprobación de) varios objetivos de un mismo evaluado en
	 * una sola llamada (endpoint real `PUT .../team/processes/:id/objectives/update-approval`,
	 * B5b). Actualiza el estado interno de cada objetivo, igual que
	 * {@link approveTeamObjective}, y devuelve la lista actualizada envuelta en
	 * `{ data }` (la forma real: `$this->success($objectives->values())`).
	 */
	approveTeamObjectivesMany(dto: any): any {
		const ids: string[] = (dto?.objective_ids || []).map((id: any) => String(id));
		const approved = dto?.approved !== false;
		const now = new Date().toISOString();
		const status = approved
			? { id: 90, name: "Aprobado", code: "approved" }
			: { id: 89, name: "Pendiente de revisión", code: "sent" };

		const updated: any[] = [];
		this.teamObjectives = this.teamObjectives.map((goal: any) => {
			if (!ids.includes(String(goal.id))) return goal;
			const next = {
				...goal,
				status_id: status.id,
				status,
				approved: approved ? 1 : 0,
				approved_at: approved ? now : null,
				updated_at: now,
			};
			updated.push(next);
			return next;
		});

		return {
			data: updated,
			message: approved ? "Objetivos aprobados correctamente." : "Aprobación retirada correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Registra la nota final de cumplimiento de un objetivo del plan de un evaluado
	 * (endpoint real `PUT .../team/processes/:id/objectives/:objectiveId/review`).
	 * Guarda la nota (`review_score`), el comentario y marca el objetivo como
	 * evaluado para que el listado lo refleje al recargar. Devuelve la envoltura
	 * `{ data, message, type, http_code }` de la API real.
	 */
	reviewTeamObjective(objectiveId: number | string, dto: any): any {
		const index = this.teamObjectives.findIndex((g: any) => String(g.id) === String(objectiveId));
		if (index === -1) return { data: null, message: "Objetivo no encontrado.", type: "error", http_code: 404 };

		const now = new Date().toISOString();
		const existing = this.teamObjectives[index];
		const status = { id: 91, name: "Evaluado", code: "reviewed", color: "green-14" };

		const updated = {
			...existing,
			status_id: status.id,
			status,
			review_score: dto?.score ?? null,
			review_comment: dto?.comment ?? null,
			reviewed_at: now,
			updated_at: now,
		};
		this.teamObjectives[index] = updated;

		return {
			data: {
				id: updated.id,
				review_score: updated.review_score,
				reviewed_at: updated.reviewed_at,
				status: { name: status.name, slug: status.code },
			},
			message: "Objetivo evaluado correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/** Resuelve una taxonomía del form-data por id (devuelve un stub si no existe). */
	private resolveTaxonomy(list: any[], id: number | string) {
		return (
			list.find((item) => String(item.id) === String(id)) || {
				id: id ?? null,
				code: null,
				name: "",
			}
		);
	}

	/**
	 * Normaliza el cuerpo del POST (estructura de escritura del backend) a la
	 * forma del GET que consumen las vistas, para que el objetivo recién creado
	 * se vea igual que los que llegan del listado.
	 */
	private normalizeObjective(dto: any, id: number | string): any {
		// `measurement_metric_values` es un arreglo: un valor por marcador hijo.
		const markerValues = Array.isArray(dto.measurement_metric_values)
			? dto.measurement_metric_values
			: [];
		return {
			id,
			name: dto.name ?? "",
			description: dto.description ?? "",
			measurement_unit: this.resolveTaxonomy(this.objectiveFormData.measurement_units, dto.measurement_unit_id),
			measurement_metric: this.resolveTaxonomy(this.objectiveFormData.measurement_metrics, dto.measurement_metric_id),
			evaluation_type: this.resolveTaxonomy(this.objectiveFormData.evaluation_types, dto.evaluation_type_id),
			measurement_value: dto.measurement_value ?? 0,
			measurement_metric_values: markerValues,
			objective_weight: dto.objective_weight ?? 0,
			status: { id: 1, code: "sent", name: "Enviado", color: "#FF9800" },
			approved: false,
			starts_at: dto.starts_at ?? null,
			finishes_at: dto.finishes_at ?? null,
			// Evidencia de seguimiento (comentario + archivo). Se conserva para que la
			// vista de seguimiento en solo lectura pueda mostrar lo ya registrado.
			evidence: dto.evidence ?? null,
			initiatives: (dto.initiatives || []).map((it: any, i: number) => ({
				id: `new-${id}-${i}`,
				active: "1",
				description: it.description ?? it.name ?? "",
			})),
		};
	}

	addObjective(dto: any): any {
		const goal = this.normalizeObjective(dto, this.nextGoalId++);
		this.myGoals.push(goal);
		return goal;
	}

	updateObjective(goalId: number | string, dto: any): any {
		// Busca primero en "mis objetivos" (caso personal) y luego en el equipo.
		const myIndex = this.myGoals.findIndex((g: any) => String(g.id) === String(goalId));
		if (myIndex !== -1) {
			this.myGoals[myIndex] = this.normalizeObjective(dto, this.myGoals[myIndex].id);
			return this.myGoals[myIndex];
		}
		const index = this.goals.findIndex((g) => String(g.id) === String(goalId));
		if (index === -1) return null;
		this.goals[index] = { ...this.goals[index], ...dto, id: this.goals[index].id };
		return this.goals[index];
	}

	deleteObjective(goalId: number | string): boolean {
		// Busca primero en "mis objetivos" (caso personal) y luego en el equipo.
		const myBefore = this.myGoals.length;
		this.myGoals = this.myGoals.filter((g: any) => String(g.id) !== String(goalId));
		if (this.myGoals.length < myBefore) return true;

		const before = this.goals.length;
		this.goals = this.goals.filter((g) => String(g.id) !== String(goalId));
		return this.goals.length < before;
	}

	/**
	 * Evidencias recién subidas, con la forma `ApiAttachment` que devuelve el backend.
	 * El multipart llega al mock con los archivos reducidos a su nombre (ver
	 * `formDataToObject`), así que el peso no se conoce y se omite.
	 */
	private buildTrackingAttachments(names: any): any[] {
		return (Array.isArray(names) ? names : names ? [names] : []).map((name: any) => ({
			id: this.nextTrackingAttachmentId++,
			name: String(name),
			url: `https://cursalab-cdn.sfo3.digitaloceanspaces.com/mock/${name}`,
			mime: null,
			size: null,
			size_label: null,
			uploaded_at: new Date().toISOString(),
		}));
	}

	/**
	 * Registra un punto de seguimiento (tracking) de un objetivo propio: lo apila en
	 * el historial (para que {@link listObjectiveTrackings} lo devuelva) y retorna la
	 * envoltura `{ data, message, type, http_code }` con la forma del backend real.
	 * El `self_review_progress_percentage` lo calcularía el backend a partir del
	 * valor y la escala del objetivo; aquí se devuelve un mock.
	 */
	trackObjective(processId: number | string, objectiveId: number | string, dto: any): any {
		const value = dto?.self_review_progress_value ?? null;
		const comment = dto?.self_review_progress_comment ?? null;

		const now = new Date().toISOString();
		const tracking = {
			id: this.nextTrackingId++,
			process_id: Number(processId),
			reviewee_id: 11,
			objective_id: Number(objectiveId),
			self_review_progress_percentage: value != null ? Number(value) : null,
			self_review_progress_value: value != null ? String(value) : null,
			self_review_progress_comment: comment,
			attachments: this.buildTrackingAttachments(dto?.attachments),
			created_at: now,
			updated_at: now,
			deleted_at: null,
		};
		this.objectiveTrackings.push(tracking);

		return {
			data: { ...tracking, self_review_progress_percentage: value != null ? String(value) : null },
			message: "Seguimiento registrado correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Lista los seguimientos (tracking) de un objetivo, ordenados del más reciente al
	 * más antiguo y paginados. Devuelve la envoltura paginada
	 * `{ data: { current_page, data, ... }, ... }` igual al backend real.
	 */
	listObjectiveTrackings(
		_processId: number | string,
		objectiveId: number | string,
		page?: string | null,
		perPage?: string | null,
	): any {
		const currentPage = Math.max(1, Number(page) || 1);
		const itemsPerPage = Math.min(100, Math.max(1, Number(perPage) || 15));

		const all = this.objectiveTrackings
			.filter((t) => String(t.objective_id) === String(objectiveId) && !t.deleted_at)
			.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

		const total = all.length;
		const lastPage = Math.max(1, Math.ceil(total / itemsPerPage));
		const start = (currentPage - 1) * itemsPerPage;
		const items = all.slice(start, start + itemsPerPage);

		return {
			data: {
				current_page: currentPage,
				data: items,
				first_page_url: null,
				from: items.length ? start + 1 : null,
				last_page: lastPage,
				last_page_url: null,
				links: [],
				next_page_url: currentPage < lastPage ? "" : null,
				path: "",
				per_page: itemsPerPage,
				prev_page_url: currentPage > 1 ? "" : null,
				to: items.length ? start + items.length : null,
				total,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Actualiza un seguimiento (tracking) ya registrado: nuevo valor de avance y
	 * comentario. Devuelve la envoltura `{ data, message, type, http_code }` con la
	 * forma del backend real (el porcentaje recalculado lo devolvería el backend).
	 */
	updateObjectiveTracking(objectiveId: number | string, trackingId: number | string, dto: any): any {
		const tracking = this.objectiveTrackings.find(
			(t) => String(t.id) === String(trackingId) && String(t.objective_id) === String(objectiveId) && !t.deleted_at,
		);
		if (!tracking) {
			return { data: null, message: "Seguimiento no encontrado.", type: "error", http_code: 404 };
		}

		const value = dto?.self_review_progress_value ?? null;
		const comment = dto?.self_review_progress_comment ?? null;
		tracking.self_review_progress_value = value != null ? String(value) : null;
		tracking.self_review_progress_percentage = value != null ? Number(value) : null;
		tracking.self_review_progress_comment = comment;
		// Las evidencias ya cargadas se conservan salvo las que se listen para quitar;
		// las nuevas se agregan al final (igual que el backend real).
		const removed = new Set((dto?.remove_attachment_ids ?? []).map((id: any) => String(id)));
		tracking.attachments = [
			...(tracking.attachments ?? []).filter((att) => !removed.has(String(att.id))),
			...this.buildTrackingAttachments(dto?.attachments),
		];
		tracking.updated_at = new Date().toISOString();

		return {
			data: { ...tracking, self_review_progress_percentage: value != null ? String(value) : null },
			message: "Seguimiento actualizado correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Elimina un seguimiento (tracking) de un objetivo. Devuelve la envoltura
	 * `{ message, type, http_code }` de la API real (sin `data`).
	 */
	deleteObjectiveTracking(objectiveId: number | string, trackingId: number | string): any {
		const before = this.objectiveTrackings.length;
		this.objectiveTrackings = this.objectiveTrackings.filter(
			(t) => !(String(t.id) === String(trackingId) && String(t.objective_id) === String(objectiveId)),
		);
		const deleted = this.objectiveTrackings.length < before;
		return {
			message: deleted ? "Seguimiento eliminado correctamente." : "Seguimiento no encontrado.",
			type: deleted ? "success" : "error",
			http_code: deleted ? 200 : 404,
		};
	}

	/** Contador para asignar ids a las revisiones de objetivos registradas. */
	private nextObjectiveReviewId = 8;

	/**
	 * Registra (mock) la revisión/autoevaluación de MIS objetivos (una nota +
	 * comentario por objetivo). Calcula un `score` promedio simple a partir de las
	 * notas enviadas y devuelve la envoltura `{ data, message, type, http_code }`
	 * con la forma documentada del backend real.
	 */
	submitObjectiveReviews(_processId?: number | string, payload?: any): any {
		const items: any[] = Array.isArray(payload?.objectives) ? payload.objectives : [];
		const scores = items
			.map((item) => Number(item?.score))
			.filter((score) => !isNaN(score));
		const avg = scores.length
			? Math.round((scores.reduce((total, score) => total + score, 0) / scores.length) * 10) / 10
			: 0;
		const issuedAt = payload?.issued_at ?? new Date().toISOString();

		return {
			data: {
				id: this.nextObjectiveReviewId++,
				score: avg,
				issued_at: issuedAt,
				created_at: issuedAt,
			},
			message: "Autoevaluación de objetivos registrada correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Evaluados del supervisor en el proceso, en la forma paginada y envuelta de la
	 * API real (`{ data: { data: TeamReviewee[], ... } }`). Aplica los filtros
	 * opcionales `q` (por nombre) y `status` (pending/in_progress/completed) como
	 * lo hace el backend; el mock no pagina (devuelve todo en una página).
	 */
	listTeamReviewees(
		_processId?: number | string,
		filters?: { q?: string | null; status?: string | null; page?: string | null; perPage?: string | null },
	): any {
		let items = [...this.teamReviewees];

		const q = filters?.q?.trim().toLowerCase();
		if (q) {
			items = items.filter((r) => r.avatar_name?.title?.toLowerCase().includes(q));
		}

		const status = filters?.status?.trim().toLowerCase();
		if (status) {
			items = items.filter((r) =>
				status === "completed"
					? r.status?.color === "green"
					: r.status?.color !== "green",
			);
		}

		// Paginación: el backend define el tamaño de página e ignora `per_page` del
		// cliente, así que el mock usa su propio tamaño fijo (rebana por `page`).
		const total = items.length;
		const perPage = 8;
		const lastPage = Math.max(1, Math.ceil(total / perPage));
		const page = Math.min(Math.max(1, Number(filters?.page) || 1), lastPage);
		const start = (page - 1) * perPage;
		const pageItems = items.slice(start, start + perPage);

		return {
			data: {
				current_page: page,
				data: pageItems,
				first_page_url: null,
				from: total ? start + 1 : null,
				last_page: lastPage,
				last_page_url: null,
				links: [],
				next_page_url: page < lastPage ? `?page=${page + 1}` : null,
				path: "",
				per_page: perPage,
				prev_page_url: page > 1 ? `?page=${page - 1}` : null,
				to: total ? start + pageItems.length : null,
				total,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Evaluados a calificar en la etapa de revisión/evaluación de objetivos. Es un
	 * endpoint propio de esa etapa (separado de {@link listTeamReviewees}); mientras
	 * el backend no exponga uno distinto, el mock devuelve el mismo equipo con la
	 * misma paginación/filtros.
	 */
	listEvaluationReviewees(
		processId?: number | string,
		filters?: { q?: string | null; status?: string | null; page?: string | null; perPage?: string | null },
	): any {
		return this.listTeamReviewees(processId, filters);
	}

	/** Aprueba el plan de objetivos de uno o varios evaluados (status -> Aprobado/green). */
	approveTeamGoals(memberIds: Array<number | string>): TeamReviewee[] {
		const ids = memberIds.map((id) => String(id));
		this.teamReviewees.forEach((member) => {
			if (ids.includes(String(member.id))) {
				member.status = { name: "Aprobado", color: "green" };
			}
		});
		return this.teamReviewees;
	}

	getEvaluationTeam(_processId?: number | string): CompetencyEvaluationTeam {
		return this.evaluationTeam;
	}

	getCompetencyEvaluation(_personId?: number | string): CompetencyEvaluation {
		return this.competencyEvaluation;
	}

	/** Competencias a autoevaluar del proceso (envoltura `{ data }` de la API real). */
	getSelfAssessmentCompetencies(_processId?: number | string): any {
		return {
			data: this.selfAssessmentCompetencies,
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Autoevaluación ya realizada (forma cruda de `competency-reviews/show`).
	 * Empieza en null (aún no realizada); se llena al enviar una autoevaluación.
	 */
	private selfAssessmentReview: any = null;

	/** Detalle de la autoevaluación ya realizada (o `data: null` si aún no existe). */
	getSelfAssessmentReview(_processId?: number | string): any {
		return {
			data: this.selfAssessmentReview,
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/** Construye el detalle (forma `competency-reviews/show`) a partir del payload. */
	private buildSelfAssessmentReview(payload?: any): any {
		const items: any[] = Array.isArray(payload?.competencies) ? payload.competencies : [];
		const competencies = items.map((item, i) => {
			const source = this.selfAssessmentCompetencies.find(
				(c) => String(c.competency_id) === String(item.competency_id),
			);
			const options = source?.scale_type?.values ?? [];
			const chosen = options.find((o) => String(o.id) === String(item.value_id));
			return {
				id: 1000 + i,
				competency_id: item.competency_id,
				competence_name: source?.name ?? "",
				competence_description: source?.description ?? "",
				review_value: null,
				value_id: chosen ? chosen.id : null,
				review_comment: item.comment ?? null,
				scale_type_name: source?.scale_type?.name ?? "",
				scale_options: options.map((o) => ({
					id: o.id,
					name: o.name,
					color: o.color,
					position: o.position,
					value: Math.round(Number(o.value) || 0),
					description: o.description,
				})),
			};
		});

		return {
			id: 102,
			qualification_type_max: 100,
			reviewee: { id: 11, name: "Liliana Cursalab" },
			reviewer: { id: 11, name: "Liliana Cursalab" },
			review_type: { id: 12, name: "Auto evaluación" },
			status: null,
			avg_score: null,
			starts_at: null,
			finishes_at: null,
			issued_at: payload?.issued_at ?? null,
			updated_at: null,
			competencies,
		};
	}

	/**
	 * Registra (mock) la autoevaluación de competencias: construye el detalle con
	 * la forma de `competency-reviews/show` a partir del payload y lo guarda para
	 * que `getSelfAssessmentReview` lo devuelva luego. Responde la envoltura
	 * `{ data }` de la API real.
	 */
	submitSelfAssessment(_processId?: number | string, payload?: any): any {
		this.selfAssessmentReview = this.buildSelfAssessmentReview(payload);
		return {
			data: this.selfAssessmentReview,
			message: "Autoevaluación registrada",
			type: "success",
			http_code: 200,
		};
	}

	/** Actualiza (mock) la autoevaluación ya registrada con el nuevo payload. */
	updateSelfAssessment(_processId?: number | string, payload?: any): any {
		this.selfAssessmentReview = this.buildSelfAssessmentReview(payload);
		return {
			data: this.selfAssessmentReview,
			message: "Autoevaluación actualizada",
			type: "success",
			http_code: 200,
		};
	}

	// ---- Feedback (vista del líder) ----------------------------------------
	// Los handlers imitan la forma EXACTA de la API v2 (snake_case dentro de
	// `{ data }`, incluidos sus huecos), así que lo que se ve en local es lo mismo
	// que se verá al apagar `MOCK_PERFORMANCE_API`.

	/** Filas que pasan los filtros activos del listado del equipo. */
	private filterFeedbackTeam(filters: Record<string, string | null>): FeedbackTeamRow[] {
		const q = (filters["q"] ?? "").trim().toLowerCase();
		const session = filters["session"] ?? "";
		const approval = filters["approval"] ?? "";
		const status = filters["status"] ?? "";
		const criteria = filters["criteria"] ?? "";

		return this.feedbackTeam.filter((row) => {
			if (q && !row.fullname.toLowerCase().includes(q)) return false;
			if (session === "with" && !row.has_feedback) return false;
			if (session === "without" && row.has_feedback) return false;
			if (approval && row.approval?.code !== approval) return false;
			if (status && row.feedback?.status?.code !== status) return false;
			if (criteria && row.criteria !== criteria) return false;
			return true;
		});
	}

	/**
	 * Listado del equipo en la etapa de feedback, filtrado y paginado
	 * **server-side** como el endpoint real (envoltura
	 * `{ data: { current_page, data: [...], … } }`).
	 */
	getFeedbackTeamRows(
		page?: string | null,
		perPage?: string | null,
		filters: Record<string, string | null> = {},
	) {
		const currentPage = Math.max(1, Number(page) || 1);
		const size = Math.max(1, Number(perPage) || 15);
		const items = this.filterFeedbackTeam(filters);
		const startIndex = (currentPage - 1) * size;

		return {
			data: {
				current_page: currentPage,
				data: items.slice(startIndex, startIndex + size),
				last_page: Math.max(1, Math.ceil(items.length / size)),
				per_page: size,
				total: items.length,
			},
		};
	}

	/**
	 * Opciones de filtro con sus conteos. Van **siempre sobre el equipo entero**,
	 * sin aplicar los filtros activos: un contador recalculado sobre lo ya
	 * filtrado dejaría de decir cuánta gente hay en cada estado.
	 */
	getFeedbackFilters() {
		const team = this.feedbackTeam;
		const countBy = (predicate: (row: FeedbackTeamRow) => boolean) => team.filter(predicate).length;

		const statuses = [FEEDBACK_STATUS_PENDING, FEEDBACK_STATUS_DONE, FEEDBACK_STATUS_CANCELLED].map(
			(status) => ({
				id: status.id,
				name: status.name,
				code: status.code,
				color: status.color,
				count: countBy((row) => row.feedback?.status?.code === status.code),
			}),
		);

		const criteriaValues = Array.from(
			team.reduce((acc, row) => {
				const value = row.criteria ?? "";
				if (value) acc.set(value, (acc.get(value) ?? 0) + 1);
				return acc;
			}, new Map<string, number>()),
			([value, count]) => ({ value, count }),
		);

		return {
			data: {
				criteria_label: team[0]?.criteria_label ?? "Puesto",
				total: team.length,
				session: [
					{ code: "with", name: "Con reunión configurada", count: countBy((row) => row.has_feedback) },
					{ code: "without", name: "Sin reunión configurada", count: countBy((row) => !row.has_feedback) },
				],
				approval: [
					{ code: "approved", name: "Aprobado", color: "primary", count: countBy((row) => row.approval?.code === "approved") },
					{ code: "failed", name: "Desaprobado", color: "negative", count: countBy((row) => row.approval?.code === "failed") },
					{ code: "pending", name: "Sin nota", color: "grey", count: countBy((row) => row.approval?.code === "pending") },
				],
				statuses,
				criteria_values: criteriaValues,
			},
		};
	}

	/**
	 * Con qué se puede agendar la reunión. Poner `can_create_room` en `false` para
	 * previsualizar el caso "el workspace no tiene cuentas": el modal pasa a pedir
	 * el enlace de Zoom escrito a mano.
	 */
	getFeedbackSchedulingOptions() {
		return {
			data: {
				can_create_room: true,
				default_duration: 45,
				default_type: { id: 5, name: "Clase", code: "class" },
				types: [
					{ id: 5, name: "Clase", code: "class", services: [{ id: 12, name: "Zoom", code: "zoom" }] },
					{ id: 6, name: "Sala", code: "room", services: [] },
				],
			},
		};
	}

	/** Guía guardada de una sesión; si nunca se guardó ninguna, arranca vacía. */
	private getFeedbackGuide(feedbackId: number | string): FeedbackBlock[] {
		return this.feedbackGuides[String(feedbackId)] ?? [];
	}

	/** Fila del equipo dueña de una sesión de feedback. */
	private findRowByFeedbackId(feedbackId: number | string): FeedbackTeamRow | undefined {
		return this.feedbackTeam.find(
			(row) => row.feedback && String(row.feedback.id) === String(feedbackId),
		);
	}

	/** Avance de los puntos de una guía. */
	private guideProgress(blocks: FeedbackBlock[]): FeedbackPointsProgress {
		const all = blocks.flatMap((block) => block.points);
		const total = all.length;
		const checked = all.filter((point) => point.checked).length;
		return { checked, total, percent: total ? Math.round((checked / total) * 100) : 0 };
	}

	/**
	 * La sesión, el resultado del evaluado y su guía de puntos, en la forma de
	 * `GET …/feedbacks/{feedbackId}/show`.
	 *
	 * Reproduce la respuesta real, **incluidos sus huecos**: `results` va como
	 * hermano de `feedback` y la grabación con su envoltorio completo. No simula
	 * `chip_label`/`allow_notes` de los bloques ni `PUT …/follow-up`.
	 */
	getFeedbackDetail(feedbackId: number | string) {
		const row = this.findRowByFeedbackId(feedbackId);
		const session = row?.feedback;
		const blocks = this.getFeedbackGuide(feedbackId);

		return {
			data: {
				feedback: {
					id: feedbackId,
					title: session?.title ?? "Feedback de cierre",
					user_id: row?.user_id ?? null,
					reviewee_fullname: row?.fullname ?? "",
					reviewer_fullname: "Carlos Vega",
					reviewee_name: row?.avatar_name ?? null,
					session_date: session?.session_date ?? null,
					session_date_raw: session?.session_date_raw ?? null,
					duration: session?.duration ?? null,
					session_type: session?.session_type ?? null,
					meeting_platform: null,
					meeting_link: session?.meeting_link ?? null,
					address: session?.address ?? null,
					address_reference: session?.address_reference ?? null,
					latitude: session?.latitude ?? null,
					longitude: session?.longitude ?? null,
					status: session?.status ?? null,
					allow_recording: session?.allow_recording ?? true,
					acceptance_requirement: session?.acceptance_requirement ?? "none",
					accepted: session?.accepted ?? false,
					accepted_at: session?.accepted_at ?? null,
					points_progress: this.guideProgress(blocks),
					...this.getFeedbackSessionTimes(feedbackId),

					position: row?.criteria ?? null,
					...this.feedbackDetailExtras,
				},
				blocks,
				// El resultado es HERMANO de `feedback`, no va dentro de él.
				results: {
					score: row?.score ?? null,
					approval: row?.approval ?? null,
					...this.feedbackDetailResults,
				},
				signature: null,
				// Mismo envoltorio que `…/recording`: la vista lee `allowed` de acá y
				// ya no pide el endpoint aparte al cargar.
				recording: this.getFeedbackRecording(feedbackId).data,
			},
		};
	}

	/**
	 * La guía que "propone ISA". No guarda nada: devuelve siempre la plantilla con
	 * los ids en `null`, igual que el endpoint real, y el líder decide si la usa.
	 *
	 * Para previsualizar la IA apagada, devolver `enabled: false` con un `reason`.
	 */
	previewFeedbackPoints() {
		return {
			data: {
				blocks: JSON.parse(JSON.stringify(PerformanceMockDb.FEEDBACK_GUIDE_TEMPLATE)),
				generated: true,
				enabled: true,
				reason: null,
			},
		};
	}

	/**
	 * Guarda la guía de una sesión: reagrupa el `points[]` plano por `block_code`
	 * conservando el orden, aplica las `block_notes` y **elimina** los puntos que
	 * no vinieron en la lista. Los que llegan con `id` conservan su marcado.
	 */
	saveFeedbackPoints(feedbackId: number | string, body: any) {
		const previous = this.getFeedbackGuide(feedbackId);
		const previousById = new Map(
			previous.flatMap((block) => block.points).map((point) => [String(point.id), point]),
		);

		const blocks: FeedbackBlock[] = [];
		(body?.points ?? []).forEach((raw: any) => {
			const code = String(raw?.block_code ?? "general");
			let block = blocks.find((item) => item.block_code === code);
			if (!block) {
				block = {
					block_code: code,
					title: raw?.block_title ?? code,
					tone: raw?.block_tone ?? "neutral",
					notes: body?.block_notes?.[code] ?? null,
					points: [],
				};
				blocks.push(block);
			}

			const kept = raw?.id != null ? previousById.get(String(raw.id)) : undefined;
			block.points.push({
				id: raw?.id ?? ++this.feedbackPointSeq,
				text: String(raw?.text ?? ""),
				detail: raw?.detail ?? null,
				source: raw?.source ?? "manual",
				checked: kept?.checked ?? false,
				checked_at: kept?.checked_at ?? null,
			});
		});

		// Un bloque con nota pero sin puntos sobrevive: el `points[]` plano no puede
		// declararlo, así que se recupera de las `block_notes`.
		// TODO (backend): confirmar que el endpoint real hace lo mismo.
		Object.entries(body?.block_notes ?? {}).forEach(([code, notes]) => {
			if (blocks.some((block) => block.block_code === code)) return;
			const old = previous.find((block) => block.block_code === code);
			blocks.push({
				block_code: code,
				title: old?.title ?? code,
				tone: old?.tone ?? "neutral",
				notes: notes as string,
				points: [],
			});
		});

		this.feedbackGuides[String(feedbackId)] = blocks;
		return { data: { blocks, progress: this.guideProgress(blocks) } };
	}

	/**
	 * Marca/desmarca un punto de la guía y devuelve el avance recalculado, en la
	 * **forma real** del backend (`{ data: { checked, total, percent } }`).
	 * Desmarcar borra también `checked_at`, igual que el endpoint real.
	 */
	setFeedbackPointChecked(feedbackId: number | string, pointId: number | string, checked: boolean) {
		const blocks = this.getFeedbackGuide(feedbackId);
		for (const block of blocks) {
			const point = block.points.find((item) => String(item.id) === String(pointId));
			if (point) {
				point.checked = !!checked;
				point.checked_at = checked ? this.formatFeedbackDateTime(new Date()) : null;
				break;
			}
		}
		return { data: this.guideProgress(blocks) };
	}

	/** Marcas de inicio/cierre de una sesión; sin tocar, las tres van en `null`. */
	private getFeedbackSessionTimes(feedbackId: number | string) {
		return (
			this.feedbackSessionTimes[String(feedbackId)] ?? {
				started_at: null,
				finished_at: null,
				finished_source: null,
			}
		);
	}

	/**
	 * Marca el inicio de la reunión. **Idempotente**: repetirlo no reescribe el
	 * `started_at`, pero sí reabre una sesión ya cerrada (es lo que hace el
	 * endpoint real con una sesión vencida).
	 */
	startFeedbackSession(feedbackId: number | string) {
		const row = this.findRowByFeedbackId(feedbackId);
		const times = this.getFeedbackSessionTimes(feedbackId);

		this.feedbackSessionTimes[String(feedbackId)] = {
			started_at: times.started_at ?? this.formatFeedbackDateTime(new Date()),
			finished_at: null,
			finished_source: null,
		};
		if (row?.feedback) row.feedback.status = FEEDBACK_STATUS_STARTED;

		return { data: this.getFeedbackDetail(feedbackId).data.feedback };
	}

	/**
	 * Cierra la reunión a mano. **Idempotente**: si ya estaba cerrada devuelve lo
	 * mismo sin reescribir el `finished_at`.
	 */
	finishFeedbackSession(feedbackId: number | string) {
		const row = this.findRowByFeedbackId(feedbackId);
		const times = this.getFeedbackSessionTimes(feedbackId);
		const now = this.formatFeedbackDateTime(new Date());

		this.feedbackSessionTimes[String(feedbackId)] = {
			started_at: times.started_at ?? now,
			finished_at: times.finished_at ?? now,
			finished_source: times.finished_source ?? "manual",
		};
		if (row?.feedback) row.feedback.status = FEEDBACK_STATUS_DONE;

		return { data: this.getFeedbackDetail(feedbackId).data.feedback };
	}

	/** `dd/MM/yyyy HH:mm`, el formato de fecha que devuelve esta etapa. */
	private formatFeedbackDateTime(date: Date): string {
		const pad = (value: number) => String(value).padStart(2, "0");
		return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
	}

	/** `yyyy-MM-dd HH:mm[:ss]` → `dd/MM/yyyy HH:mm`, como lo devuelve el listado. */
	private toFeedbackDisplayDate(raw?: string | null): string | null {
		if (!raw) return null;
		const [date, time = ""] = raw.trim().split(" ");
		const [year, month, day] = date.split("-");
		if (!year || !month || !day) return raw;
		return `${day}/${month}/${year} ${time.slice(0, 5)}`.trim();
	}

	/**
	 * Agenda la sesión de un integrante y la persiste en el store en memoria, para
	 * que la recarga del listado la refleje. Si el líder armó la guía en el paso 2
	 * del modal, se guarda junto con la sesión (llave `points`).
	 *
	 * El cuerpo llega con las llaves del backend y el store guarda la fila con esas
	 * mismas llaves: es una copia campo a campo, sin traducción.
	 */
	saveFeedbackMeeting(request: FeedbackMeetingRequest) {
		const row = this.feedbackTeam.find(
			(member) => String(member.user_id) === String(request.reviewee_id),
		);
		if (!row) return { data: null };

		const feedbackId = row.feedback?.id ?? ++this.feedbackSessionSeq;
		row.has_feedback = true;
		row.feedback = {
			id: feedbackId,
			title: request.title ?? row.feedback?.title ?? "Feedback de cierre",
			session_date: this.toFeedbackDisplayDate(request.session_date),
			session_date_raw: (request.session_date ?? "").slice(0, 16),
			duration: request.duration ?? null,
			session_type: request.session_type,
			meeting_platform: request.meeting ? "zoom" : null,
			meeting_link: request.meeting_link ?? null,
			address: request.address ?? null,
			acceptance_requirement: request.feedback_confirmation_type ?? "none",
			accepted: row.feedback?.accepted ?? false,
			accepted_at: row.feedback?.accepted_at ?? null,
			points_progress: { checked: 0, total: 0, percent: 0 },
			status: FEEDBACK_STATUS_PENDING,
			// `address_reference`, `latitude`, `longitude` y `allow_recording` se
			// reciben pero NO se devuelven: es el hueco real del backend (ver el TODO
			// de `FeedbackTeamSession`). No agregarlos acá "para que funcione".
		};

		if (request.points?.length) {
			this.saveFeedbackPoints(feedbackId, { points: request.points });
		}
		row.feedback.points_progress = this.guideProgress(this.getFeedbackGuide(feedbackId));

		return { data: row.feedback };
	}

	// ---- Sesiones de seguimiento (check-ins) del líder ----------------------
	// Solo en memoria: el backend real ya las sirve (`team/processes/{id}/checkins`).

	private checkinSeq = 0;
	private checkins: Checkin[] = [];

	private static readonly CHECKIN_STATUS = {
		pending: { id: 1, code: "pending", name: "Pendiente", color: "orange" },
		cancelled: { id: 5, code: "cancelled", name: "Cancelada", color: "grey" },
	} as const;

	/** Las dos etapas en que se agenda, ambas en curso, y el historial de la persona. */
	getCheckins(revieweeId: number | string) {
		const running = { id: 2, code: "running", name: "En curso", position: 2 };
		return {
			data: {
				stages: [
					{ code: "objectives", name: "Objetivos", status: running, can_schedule: true },
					{ code: "tracking", name: "Seguimiento", status: running, can_schedule: true },
				],
				checkins: this.checkins
					.filter((checkin) => String(checkin.reviewee_id) === String(revieweeId))
					.sort((a, b) => (a.scheduled_at_raw ?? "").localeCompare(b.scheduled_at_raw ?? "")),
			},
		};
	}

	saveCheckin(request: CheckinStoreRequest) {
		const raw = (request.scheduled_at ?? "").slice(0, 16);
		const checkin: Checkin = {
			id: ++this.checkinSeq,
			process_id: 1,
			process_stage_code: request.process_stage_code,
			stage_name: request.process_stage_code === "objectives" ? "Objetivos" : "Seguimiento",
			reviewee_id: Number(request.reviewee_id),
			reviewee_name: null,
			reviewer_id: 1,
			reviewer_name: "Tú",
			title: request.title || "Sesión de seguimiento",
			agenda: request.agenda ?? null,
			scheduled_at: null,
			scheduled_at_raw: raw,
			duration: request.duration ?? null,
			session_type: request.session_type ?? null,
			meeting_id: null,
			meeting_link: request.meeting_link ?? null,
			address: request.address ?? null,
			status: { ...PerformanceMockDb.CHECKIN_STATUS.pending },
			started_at: null,
			finished_at: null,
			finished_source: null,
			auto_close_deadline: null,
			notes: null,
			notified_at: new Date().toISOString(),
			cancelled_at: null,
			acknowledged_at: null,
			acknowledgement_comment: null,
			created_at: new Date().toISOString(),
		};
		this.checkins.push(checkin);
		return { data: checkin };
	}

	updateCheckin(checkinId: number | string, request: CheckinUpdateRequest) {
		const checkin = this.checkins.find((item) => String(item.id) === String(checkinId));
		if (!checkin) return { data: null };
		if (request.scheduled_at) checkin.scheduled_at_raw = request.scheduled_at.slice(0, 16);
		if (request.title) checkin.title = request.title;
		if (request.duration) checkin.duration = request.duration;
		if (request.agenda !== undefined) checkin.agenda = request.agenda;
		return { data: checkin };
	}

	cancelCheckin(checkinId: number | string) {
		const checkin = this.checkins.find((item) => String(item.id) === String(checkinId));
		if (!checkin) return { data: null };
		checkin.status = { ...PerformanceMockDb.CHECKIN_STATUS.cancelled };
		checkin.cancelled_at = new Date().toISOString();
		return { data: checkin };
	}

	// ---- Feedback · grabación de la reunión --------------------------------

	/**
	 * Guarda el audio subido por el líder. Como el mock vive en memoria, la URL
	 * reproducible se genera con `URL.createObjectURL` sobre el propio archivo: el
	 * reproductor suena de verdad durante la sesión (al recargar se pierde, igual
	 * que el resto del store mock).
	 */
	saveFeedbackRecording(feedbackId: number | string, form: FormData) {
		const key = String(feedbackId);
		const audio = form?.get("recording") as File | Blob | null;
		const durationSeconds = Number(form?.get("duration_seconds") ?? 0);

		// Liberar la URL de una grabación anterior de la misma sesión.
		const previous = this.feedbackRecordings[key];
		if (previous?.url?.startsWith("blob:")) URL.revokeObjectURL(previous.url);

		const recording: ApiFeedbackRecording = {
			id: `rec-${Date.now()}`,
			status: "uploaded",
			duration_seconds: durationSeconds,
			recorded_at: this.formatFeedbackDateTime(new Date()),
			has_audio: true,
			url: audio ? URL.createObjectURL(audio) : "",
			expires_at: null,
			transcript: null,
			transcription_error: null,
			summary: null,
			summary_pending: true,
			summary_status: "pending",
		};

		this.feedbackRecordings[key] = { ...recording, uploadedAt: Date.now() };
		return { data: { allowed: true, blocked_reason: null, recording } };
	}

	/**
	 * Grabación de una sesión. Simula el trabajo de IsA: durante los primeros
	 * {@link PerformanceMockDb.SUMMARY_DELAY_MS} llega sin `transcript` ni
	 * `summary` (la vista lo deriva a "procesando") y después con los dos listos —
	 * la transcripción literal y el resumen redactado, que son textos distintos.
	 */
	getFeedbackRecording(feedbackId?: number | string) {
		const row = this.findRowByFeedbackId(feedbackId ?? "");
		const allowed = row?.feedback?.allow_recording ?? true;
		const stored = this.feedbackRecordings[String(feedbackId ?? "")];

		if (!stored) {
			return {
				data: {
					allowed,
					blocked_reason: allowed ? null : "La sesión no permite grabación.",
					recording: null,
				},
			};
		}

		const { uploadedAt, ...recording } = stored;
		if (Date.now() - uploadedAt >= PerformanceMockDb.SUMMARY_DELAY_MS) {
			stored.status = "transcribed";
			stored.transcript = PerformanceMockDb.FEEDBACK_TRANSCRIPT_TEXT;
			stored.summary = PerformanceMockDb.FEEDBACK_SUMMARY_HTML;
			stored.summary_pending = false;
			stored.summary_status = "done";
		}

		return {
			data: {
				allowed,
				blocked_reason: null,
				recording: {
					...recording,
					status: stored.status,
					transcript: stored.transcript,
					summary: stored.summary,
					summary_pending: stored.summary_pending,
					summary_status: stored.summary_status,
				},
			},
		};
	}

	// ---- Feedback (vista del colaborador: "Mi feedback") -------------------

	/**
	 * Sesión de feedback del evaluado, en la forma cruda del backend. Deriva el
	 * `status` del **fin** de la reunión (`session_date_raw` + `duration`): mientras
	 * no termine → "Agendada"; una vez terminada → "Completada". Así ambos estados
	 * son alcanzables cambiando esas dos llaves en el store, y el mock no dice
	 * "Completada" a mitad de reunión —que es cuando la vista todavía la muestra
	 * agendada— (ver `MyFeedbackContentComponent.isScheduled`).
	 *
	 * Para previsualizar el estado "sin reunión agendada", devolver
	 * `{ data: { ...this.myFeedback, feedback: null } }`.
	 */
	getMyFeedback(): { data: any } {
		const session = this.myFeedback.feedback;
		if (!session) return { data: { feedback: null, results_available: false, results: null, blocks: [] } };

		const completed = !this.isSessionInFuture(session.session_date_raw, session.duration);
		session.status = completed ? FEEDBACK_STATUS_DONE : FEEDBACK_STATUS_SCHEDULED;
		// Igual que el backend: completada y, si pide conformidad, ya aceptada.
		const available = completed && (session.acceptance_requirement === "none" || !!session.accepted_at);
		const blocks = available ? JSON.parse(JSON.stringify(PerformanceMockDb.FEEDBACK_GUIDE_TEMPLATE)) : [];
		const row = this.feedbackTeam[0];

		return {
			data: {
				feedback: available ? { ...session, points_progress: this.guideProgress(blocks) } : session,
				signature: this.myFeedback.signature,
				signature_config: this.myFeedback.signature_config,
				// mismo envoltorio que `…/recording`
				recording: this.getFeedbackRecording(session.id).data,
				results_available: available,
				results: available ? { score: row?.score ?? null, approval: row?.approval ?? null, ...this.feedbackDetailResults } : null,
				blocks,
			},
		};
	}

	/**
	 * Conformidad del evaluado sobre su sesión de feedback. Devuelve la **forma
	 * real** del backend, igual que el resto de esta vista.
	 *
	 * El backend responde 422 si la sesión aún no está cerrada; acá se asume
	 * siempre válida. Reenviar no cambia la fecha de una conformidad ya dada.
	 */
	acceptMyFeedback(body: any) {
		const session = this.myFeedback.feedback;

		if (session && !session.accepted) {
			const now = new Date();
			const pad = (value: number) => String(value).padStart(2, "0");
			// `dd/MM/yyyy HH:mm`, el formato con el que el backend devuelve las fechas
			// de esta etapa (no ISO, como el resto del mock).
			session.accepted = true;
			session.accepted_at =
				`${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ` +
				`${pad(now.getHours())}:${pad(now.getMinutes())}`;
			session.acceptance_type = session.acceptance_requirement ?? "check";
			session.signature_name = body?.signature_name ?? "";
			session.acceptance_comment = body?.comment ?? "";
			// La firma va a nivel de respuesta, con la forma de `signaturePayload`.
			this.myFeedback.signature = body?.signature
				? { url: body.signature, signer_name: body?.signature_name ?? "", signed_at: session.accepted_at, hash: null }
				: null;
		}

		return {
			id: session?.id ?? null,
			accepted: true,
			accepted_at: session?.accepted_at ?? null,
			acceptance_type: session?.acceptance_type ?? "check",
			signature_name: session?.signature_name ?? "",
			acceptance_comment: session?.acceptance_comment ?? "",
		};
	}

	/**
	 * `true` mientras la sesión no haya terminado: su fecha `yyyy-MM-dd HH:mm` más
	 * los `duration` minutos que dure es posterior a ahora. Sin `duration` se
	 * compara solo contra la hora de inicio.
	 */
	private isSessionInFuture(sessionDate?: string | null, duration?: number | null): boolean {
		const when = new Date((sessionDate ?? "").replace(" ", "T"));
		if (isNaN(when.getTime())) return false;
		const minutes = duration != null && duration > 0 ? duration : 0;
		return when.getTime() + minutes * 60 * 1000 > Date.now();
	}

	// ---- Encuesta del proceso (etapa "survey") -----------------------------

	/**
	 * Encuesta de cierre del proceso. Cubre los cinco tipos de pregunta que sabe
	 * renderizar la vista para poder previsualizarlos todos.
	 *
	 * > **Para previsualizar el estado "ya respondida"**: cambiar `status` a
	 * > `"completed"` (y poner una `submittedAt`). Enviar la encuesta desde la
	 * > vista también deja el mock en ese estado hasta recargar la página.
	 */
	private processSurvey: PerformanceSurvey = {
		id: 900,
		title: "Encuesta del proceso de desempeño",
		description:
			"Tus respuestas son confidenciales y nos ayudan a mejorar el proceso para el siguiente periodo.",
		status: "pending",
		submittedAt: null,
		questions: [
			{
				id: 9001,
				title: "¿Qué tan claro te resultó el objetivo de este proceso de desempeño?",
				type: { code: "opcion-simple", name: "Opción simple" },
				required: true,
				options: [
					{ value: 1, text: "Muy claro" },
					{ value: 2, text: "Claro" },
					{ value: 3, text: "Poco claro" },
					{ value: 4, text: "Nada claro" },
				],
			},
			{
				id: 9002,
				title: "¿Qué etapas del proceso te resultaron más útiles?",
				type: { code: "opcion-multiple", name: "Opción múltiple" },
				required: true,
				options: [
					{ value: 1, text: "Definición de objetivos" },
					{ value: 2, text: "Seguimiento de objetivos" },
					{ value: 3, text: "Evaluación de competencias" },
					{ value: 4, text: "Reunión de feedback" },
					{ value: 5, text: "Plan de desarrollo individual" },
				],
			},
			{
				id: 9003,
				title: "¿Cómo calificarías el acompañamiento de tu líder durante el proceso?",
				type: { code: "califica", name: "Valoración" },
				required: true,
				// Sin `emoji`: la valoración se pinta con estrellas.
				options: [
					{ value: 1, text: "Muy malo" },
					{ value: 2, text: "Malo" },
					{ value: 3, text: "Regular" },
					{ value: 4, text: "Bueno" },
					{ value: 5, text: "Excelente" },
				],
			},
			{
				id: 9004,
				title: "¿Qué tan probable es que recomiendes este proceso a un colega?",
				type: { code: "nps", name: "NPS" },
				required: true,
				options: { text_min: "Nada probable", text_max: "Muy probable" },
			},
			{
				id: 9005,
				title: "¿Qué mejorarías del proceso de desempeño?",
				type: { code: "texto", name: "Texto libre" },
				required: false,
				options: null,
				maxLength: 250,
			},
		],
	};

	/** Encuesta del proceso que le corresponde responder al usuario. */
	getProcessSurvey(_processId?: number | string): { data: PerformanceSurvey } {
		return { data: this.processSurvey };
	}

	/**
	 * Recibe las respuestas y deja la encuesta como respondida (persiste en el
	 * store hasta recargar la página, como el resto de escrituras del mock).
	 */
	submitProcessSurvey(submission: SurveySubmission): { data: PerformanceSurvey } {
		this.processSurvey = {
			...this.processSurvey,
			status: "completed",
			submittedAt: submission?.issued_at ?? new Date().toISOString(),
		};
		return { data: this.processSurvey };
	}

	// ---- Etapa de Seguimiento — rol líder ----------------------------------
	// Mismas personas (y mismos `user_id`) que el listado de feedback del equipo,
	// para que el líder vea a su equipo consistente entre etapas.

	/**
	 * Objetivos del equipo con su avance frente a lo esperado. Las filas cubren los
	 * cuatro casos que la vista tiene que saber pintar, incluido un objetivo con
	 * `level: null` (sin fechas del objetivo ni de la etapa), que **no** es lo
	 * mismo que ir bien.
	 */
	private teamTrackingObjectives: TeamTrackingObjective[] = [
		{
			objective_id: 12,
			reviewee_id: 88,
			reviewee_name: "Sergio Pérez Fernández",
			name: "Colocación de cartera",
			progress: 30,
			expected: 80,
			gap: 50,
			level: "at_risk",
			submissions: 2,
			trend: 4,
		},
		{
			objective_id: 21,
			reviewee_id: 89,
			reviewee_name: "Sandra Sánchez Fernández",
			name: "Retención de clientes clave",
			progress: 55,
			expected: 70,
			gap: 15,
			level: "behind",
			submissions: 3,
			trend: -2,
		},
		{
			objective_id: 33,
			reviewee_id: 90,
			reviewee_name: "Lucía Jiménez Rodríguez",
			name: "NPS del área comercial",
			progress: 90,
			expected: 80,
			gap: 0,
			level: "on_track",
			submissions: 4,
			trend: 6,
		},
		{
			objective_id: 41,
			reviewee_id: 91,
			reviewee_name: "Patricia Fernández Pérez",
			name: "Reducción de reclamos",
			progress: 78,
			expected: 75,
			gap: 0,
			level: "on_track",
			submissions: 2,
			trend: 3,
		},
		{
			objective_id: 52,
			reviewee_id: 93,
			reviewee_name: "David Jiménez García",
			name: "Capacitación del equipo",
			progress: 100,
			expected: 90,
			gap: 0,
			level: "on_track",
			submissions: 5,
			trend: 1,
		},
		{
			// Sin fechas del objetivo ni de la etapa: el backend no puede calcular el
			// período, así que manda `level: null` y `trend: null`.
			objective_id: 64,
			reviewee_id: 92,
			reviewee_name: "Elena Jiménez Pérez",
			name: "Plan de expansión regional",
			progress: 20,
			expected: 0,
			gap: 0,
			level: null,
			submissions: 1,
			trend: null,
		},
	];

	/** Conteo por nivel de {@link teamTrackingObjectives}. */
	private get teamTrackingSummary(): TeamTrackingSummary {
		return {
			at_risk: this.teamTrackingObjectives.filter((row) => row.level === "at_risk").length,
			behind: this.teamTrackingObjectives.filter((row) => row.level === "behind").length,
			on_track: this.teamTrackingObjectives.filter((row) => row.level === "on_track").length,
			unknown: this.teamTrackingObjectives.filter((row) => row.level === "unknown" || row.level == null).length,
		};
	}

	/** Objetivos del equipo que no van a llegar, con la envoltura de la API real. */
	getTeamTrackingRisk(_processId?: number | string): TeamTrackingRiskResponse {
		return {
			data: {
				summary: this.teamTrackingSummary,
				objectives: this.teamTrackingObjectives,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Resumen de ISA del seguimiento del equipo. El mock no genera nada con IA:
	 * devuelve siempre el mismo registro con `generated: false` y
	 * `reason: "unchanged"`, y solo con `forced` responde `generated: true` con un
	 * texto distinto, para poder previsualizar el botón "Actualizar resumen".
	 */
	getTeamTrackingIsaSummary(
		processId?: number | string,
		forced = false,
	): TeamTrackingIsaSummaryResponse {
		const metrics = this.teamTrackingSummary;

		const summary: TeamTrackingIsaContent = forced
			? {
					headline: "Sergio sigue siendo la prioridad",
					summary:
						"<p>Volvimos a mirar el equipo: <strong>Sergio</strong> no movió su avance desde la última revisión y <strong>Sandra</strong> recuperó terreno.</p>",
					focus: [
						{
							reviewee_name: "Sergio Pérez Fernández",
							objective: "Colocación de cartera",
							reason: "Sigue 50 puntos por debajo y no registró avances esta semana.",
							action: "Agenda una conversación corta y define con él un compromiso para los próximos 7 días.",
						},
					],
					nothing_to_report: false,
				}
			: {
					headline: "Dos personas necesitan atención",
					summary:
						"<p>Tu equipo tiene <strong>dos</strong> objetivos por debajo de lo esperado. El resto acompaña el calendario.</p>",
					focus: [
						{
							reviewee_name: "Sergio Pérez Fernández",
							objective: "Colocación de cartera",
							reason: "Va 50 puntos por debajo de lo que el calendario pide.",
							action: "Pídele una actualización y revisen juntos si la meta sigue siendo alcanzable.",
						},
						{
							reviewee_name: "Sandra Sánchez Fernández",
							objective: "Retención de clientes clave",
							reason: "Va 15 puntos por debajo y su último registro es de hace tres semanas.",
							action: "Recuérdale registrar su avance y pregunta qué la está frenando.",
						},
					],
					nothing_to_report: false,
				};

		return {
			data: {
				record: {
					process_id: Number(processId) || 1,
					reviewer_id: 77,
					scope: "tracking",
					generated_on: new Date().toISOString().slice(0, 10),
					metrics,
					summary,
				},
				generated: forced,
				enabled: true,
				reason: forced ? "forced" : "unchanged",
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	// ---- Seguimiento del equipo — listado y detalle (diseño final) ---------
	// Mismas personas (y mismos `reviewee_id`) que el tablero de riesgo y que el
	// listado de feedback, para que el líder vea a su equipo consistente entre
	// etapas.

	/**
	 * Equipo del líder para `trackings/summary`. Ojo con lo que **no** está: el
	 * **cargo** no viaja en la respuesta real, así que el mock tampoco lo manda (el
	 * mock devuelve los huecos del backend, no los rellena).
	 */
	private teamTrackingReviewees: TeamTrackingTeamMember[] = [
		{ reviewee_id: 88, name: "Sergio Pérez Fernández", avatar_name: { avatar: null, initials: "SP", title: "Sergio Pérez Fernández" }, objectives: 3 },
		{ reviewee_id: 89, name: "Sandra Sánchez Fernández", avatar_name: { avatar: null, initials: "SS", title: "Sandra Sánchez Fernández" }, objectives: 3 },
		{ reviewee_id: 90, name: "Lucía Jiménez Rodríguez", avatar_name: { avatar: null, initials: "LJ", title: "Lucía Jiménez Rodríguez" }, objectives: 3 },
		{ reviewee_id: 91, name: "Patricia Fernández Pérez", avatar_name: { avatar: null, initials: "PF", title: "Patricia Fernández Pérez" }, objectives: 3 },
		{ reviewee_id: 92, name: "Elena Jiménez Pérez", avatar_name: { avatar: null, initials: "EJ", title: "Elena Jiménez Pérez" }, objectives: 3 },
		{ reviewee_id: 93, name: "David Jiménez García", avatar_name: { avatar: null, initials: "DJ", title: "David Jiménez García" }, objectives: 3 },
	];

	/** Contador de ids de los envíos de seguimiento que registra el líder. */
	private nextTeamTrackingId = 500;

	/**
	 * Envíos de seguimiento sobre los objetivos de los evaluados, con la forma de
	 * `submissions_list[]`.
	 *
	 * ⚠️ El plan de la etapa documenta `comment`, `attachments[]` y `created_at`;
	 * la llave del valor del avance ya se confirmó como `progress_value`.
	 */
	private teamObjectiveTrackings: any[] = [
		{
			id: 481,
			reviewee_id: 88,
			objective_id: 5,
			progress_value: 62,
			comment: "Revisamos la cartera de la zona y ya hay tres cuentas comprometidas.",
			attachments: [
				{
					id: 951,
					name: "cartera-abril.pdf",
					url: "https://cursalab-cdn.sfo3.digitaloceanspaces.com/mock/cartera-abril.pdf",
					mime: "application/pdf",
					size: 96,
					size_label: "96 KB",
					uploaded_at: "2026-06-20T15:10:00.000000Z",
				},
			],
			created_at: "2026-06-20T15:10:00.000000Z",
			updated_at: "2026-06-20T15:10:00.000000Z",
		},
		{
			id: 482,
			reviewee_id: 88,
			objective_id: 4,
			progress_value: 20,
			comment: "Primer contacto con los clientes del segmento retail.",
			attachments: [],
			created_at: "2026-06-10T12:00:00.000000Z",
			updated_at: "2026-06-10T12:00:00.000000Z",
		},
	];

	/** Contador de ids de las evidencias que suben los seguimientos del líder. */
	private nextTeamTrackingAttachmentId = 952;

	/**
	 * Equipo del líder con sus totales (`trackings/summary`). Devuelve el equipo
	 * entero, sin paginar: el buscador de la vista filtra en el front.
	 */
	getTeamTrackingTeamSummary(_processId?: number | string): TeamTrackingTeamSummaryResponse {
		return {
			data: {
				totals: {
					reviewees: this.teamTrackingReviewees.length,
					objectives: this.teamTrackingReviewees.reduce((total, row) => total + (row.objectives ?? 0), 0),
				},
				reviewees: this.teamTrackingReviewees,
			},
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Seguimiento de UNA persona: el avance de cada uno de sus objetivos aprobados
	 * con su historial de envíos. No devuelve la definición del objetivo (nombre,
	 * plazo, escala): eso lo aporta el endpoint de objetivos del equipo, y la vista
	 * cruza las dos respuestas por `objective_id`.
	 */
	getRevieweeTrackings(_processId?: number | string, revieweeId?: number | string): TeamTrackingRevieweeResponse {
		const reviewee =
			this.teamTrackingReviewees.find((row) => String(row.reviewee_id) === String(revieweeId)) ??
			this.teamTrackingReviewees[0];

		const objectives = this.teamObjectives
			.filter((objective: any) => objective?.status?.code === "approved")
			.map((objective: any) => {
				const submissions = this.teamObjectiveSubmissions(reviewee?.reviewee_id, objective.id);
				const last = submissions[submissions.length - 1];
				return {
					objective_id: objective.id,
					name: objective?.objective?.name ?? objective?.name ?? "",
					progress: last ? Number(last.progress_value) : null,
					submissions: submissions.length,
					submissions_list: submissions,
				};
			});

		return {
			data: { reviewee, objectives },
			message: "",
			type: "success",
			http_code: 200,
		};
	}

	/**
	 * Registra un avance del líder sobre el objetivo de un evaluado, con la misma
	 * forma que devuelve la API real.
	 */
	trackTeamObjective(
		_processId: number | string,
		revieweeId: number | string,
		objectiveId: number | string,
		dto: any,
	): any {
		const now = new Date().toISOString();
		const value = dto?.self_review_progress_value ?? null;

		const submission = {
			id: this.nextTeamTrackingId++,
			reviewee_id: Number(revieweeId),
			objective_id: Number(objectiveId),
			progress_value: value != null ? Number(value) : null,
			comment: dto?.self_review_progress_comment ?? null,
			attachments: this.buildTeamTrackingAttachments(dto?.attachments),
			created_at: now,
			updated_at: now,
		};
		this.teamObjectiveTrackings.push(submission);

		return {
			data: submission,
			message: "Seguimiento registrado correctamente.",
			type: "success",
			http_code: 200,
		};
	}

	/** Envíos de un objetivo de un evaluado, del más antiguo al más reciente. */
	private teamObjectiveSubmissions(revieweeId: number | string, objectiveId: number | string): any[] {
		return this.teamObjectiveTrackings
			.filter(
				(row) =>
					String(row.reviewee_id) === String(revieweeId) &&
					String(row.objective_id) === String(objectiveId),
			)
			.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
	}

	/** Evidencias recién subidas, con la forma `ApiAttachment` que devuelve el backend. */
	private buildTeamTrackingAttachments(attachments: any): ApiAttachment[] {
		const names: string[] = Array.isArray(attachments) ? attachments : [];
		return names.map((name) => ({
			id: this.nextTeamTrackingAttachmentId++,
			name,
			url: `https://cursalab-cdn.sfo3.digitaloceanspaces.com/mock/${name}`,
			mime: null,
			size: null,
			size_label: null,
			uploaded_at: new Date().toISOString(),
		}));
	}
}
